import {
    Button,
    Switch,
    Divider,
    DatePicker,
    Picker,
    Popup,
    InputNumber,
    Cell,
    Row,
    Col,
    Sticky,
    Dialog,
    Tabs,
    Input,
    Radio,
} from '@nutui/nutui-react';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Ask } from '@nutui/icons-react';
import axios from 'axios';
import { useConsumptionActivity } from '../data/consumptionActivity';
import WeiZhenExchangeView from '../components/WeiZhenExchangeView';
import { formatOpenTime, listPresetTeamSlots } from '../common';

/**
 * 页面上所有阵容下拉框（defaultTeamList）所对应的配置路径。
 * 与 onFormChange 中的 `xxx:team:teamId` 键一一对应，用于进页面时统一修正越界的阵容ID；
 * 新增阵容下拉时须在此登记，否则该项的越界值不会被修正（后端切换阵容时会抛“预设阵容ID超出范围”）。
 * @type {string[][]}
 */
const teamIdConfigPaths = [
    ['team'], // 脚本开始/结束时的默认阵容
    ['legion', 'boss', 'team'],
    ['tower', 'team'],
    ['竞技场', 'team'],
    ['每日咸王', 'team'],
    ['灯神挑战', 'team'],
    ['深海挑战', 'team'],
    ['activity', 'team'],
    ['activity', 'evoTower', 'team'],
];

function Setting(props) {
    const { user = {}, updateConfig, onConfigChangeCb, onCancelConfirmDialog, setConfirmDialog, doDailyCb } = props;
    const lightTeamOptions = [
        { value: '1', text: '魏国' },
        { value: '2', text: '蜀国' },
        { value: '3', text: '吴国' },
        { value: '4', text: '群雄' },
    ];

    const legionBossOptions = [
        { value: 0, text: '0' },
        { value: 1, text: '1' },
        { value: 2, text: '2' },
        { value: 3, text: '3' },
        { value: 4, text: '4' },
    ];

    const arenaModeOptions = [
        { value: 1, text: '随机挑战' },
        { value: 2, text: '选择最低战力' },
        { value: 3, text: '选择最高战力' },
    ];

    const bottleModeOptions = [
        { value: 0, text: '囤罐子模式' },
        { value: 1, text: '收罐子模式' },
    ];

    const legionVoteOptions = [
        { value: 0, text: '赛前6小时内随机投票' },
        { value: 1365568, text: '寰宇归一' },
        { value: 1665228, text: '临江仙紫薇' },
        { value: 704839, text: '铨村人的希望' },
        { value: 2286020, text: '自在崖' },
        { value: 267057, text: '观屿' },
        { value: 2952486, text: '歐天下' },
        { value: 891462, text: 'F·四季如春' },
        { value: 1312139, text: '无锋⁵⁰¹' },
        { value: 942724, text: '¹ʷ¹礼之用' },
        { value: 11139, text: '陷阵营³⁸³' },
        { value: 56888, text: '泡泡鱼' },
        { value: 407676, text: '星辰巅峰' },
        { value: 839595, text: 'F·鸭鸭学院' },
        { value: 1062307, text: 'F·达拉然' },
        { value: 2021392, text: '归墟' },
        { value: 266088, text: '指尖情怀' },
    ];

    const vip = user.role_info.vip;
    let maxBuyCount = 2;
    if (vip === 0) {
        maxBuyCount = 2;
    } else if (vip < 3) {
        maxBuyCount++;
    } else if (vip < 5) {
        maxBuyCount += 2;
    } else if (vip < 7) {
        maxBuyCount += 3;
    } else if (vip < 9) {
        maxBuyCount += 4;
    } else if (vip < 11) {
        maxBuyCount += 5;
    } else if (vip < 13) {
        maxBuyCount += 6;
    } else if (vip < 15) {
        maxBuyCount += 7;
    } else if (vip < 17) {
        maxBuyCount += 8;
    } else if (vip === 17) {
        maxBuyCount += 9;
    } else if (vip === 18) {
        maxBuyCount += 10;
    }

    // 预设阵容下拉项：value 与数组下标一致（0 表示不换阵容），阵容数量以角色实际拥有的槽位为准
    const presetTeamSlots = listPresetTeamSlots(user.role_info);
    // 可使用的最大阵容ID，取自槽位本身而非数组长度，避免依赖“槽位ID连续编号”这一前提
    const maxPresetTeamId = presetTeamSlots.reduce((max, slot) => Math.max(max, slot.id), 0);
    const defaultTeamList = [{ value: 0, text: '不换阵容' }].concat(
        presetTeamSlots.map(slot => ({ value: slot.id, text: slot.name || `阵容${slot.id}` }))
    );

    const fixConfigLimit = useCallback(
        config => {
            // 修正阵容ID超出范围。上限是角色实际拥有的槽位数（活动兑换的布阵槽位可超出VIP），
            // 越界值在下拉框里没有对应选项，且后端切换阵容时必然报错，故重置为阵容1并留痕便于排查
            teamIdConfigPaths.forEach(path => {
                const team = path.reduce((node, key) => node?.[key], config);
                if (team?.teamId > maxPresetTeamId) {
                    console.warn(`阵容配置 ${path.join('.')} 的 teamId ${team.teamId} 超出角色可用的最大阵容ID ${maxPresetTeamId}，已重置为阵容1`);
                    team.teamId = 1;
                }
            });
            // 修正strategies结构下的refresh限制
            if (config.buyGoods?.strategies) {
                ['default', '金砖达标', 'goldenFish'].forEach(strategy => {
                    if (config.buyGoods.strategies[strategy]?.refresh > maxBuyCount) {
                        config.buyGoods.strategies[strategy].refresh = 2;
                    }
                });
            }

            return config;
        },
        [maxPresetTeamId, maxBuyCount]
    );

    const h2Style = {
        maginTop: '30px',
        marginBottom: '10px',
        fontSize: '14px',
        padding: '0 10px',
        fontWeight: '400',
        marginTop: '20px',
    };
    const cellStyle = {
        flexWrap: 'wrap',
        borderRadius: '6px',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: 'row',
        display: 'flex',
    };

    // const [config, setConfig] = useState(user.config)
    const [visible, setVisible] = useState({
        buyGold: false,
        legionBoss: false,
        studyMode: false,
        studyCount: false,
        hangUpTime: false,
        buyGoods: false,
        legionVote: false,
        dungeonShop: false,
        buyGoodsTime: false,
        activityBuystoregoods: false,
        lightState: false,
        commandTask: false,
        useTorch: false,
        legionBuyGoods: false,
        boxActivityWeekdayPicker: false,
        battleFieldSignUpTime: false,
        useItemsVisible: false,
        exchangeTargetPicker: false,
        teamDescPicker: null, // 用于存储当前正在编辑的 teamDescId 字段路径
    });
    const [buyGoodsActiveTab, setBuyGoodsActiveTab] = useState('default');
    const [formValues, setFormValues] = useState(fixConfigLimit(user.config) || {});
    // 自选大奖奖励列表
    const [weekActRewards, setWeekActRewards] = useState({ box: [], recruit: [], gold: [] });

    // 阵容列表数据
    const [teamDescList, setTeamDescList] = useState([]);
    const [teamDescListLoading, setTeamDescListLoading] = useState(false);

    // 获取阵容列表
    useEffect(() => {
        const fetchTeamList = async () => {
            if (!user.encrypt_user) return;
            setTeamDescListLoading(true);
            try {
                const { data } = await axios.get('/v1/teams');
                if (data.code === 200 && data.body?.teams) {
                    setTeamDescList(data.body.teams);
                }
            } catch (error) {
                console.error('获取阵容列表失败:', error);
            } finally {
                setTeamDescListLoading(false);
            }
        };
        fetchTeamList();
    }, [user.encrypt_user]);

    // 阵容描述默认值（用于Picker组件正确匹配选项）
    const TEAM_DESC_DEFAULT = 'none';

    // 构建阵容描述选项列表
    const teamDescOptions = useMemo(() => {
        const options = [{ value: TEAM_DESC_DEFAULT, text: '不修改阵容' }];
        teamDescList.forEach(team => {
            options.push({ value: team.id, text: team.name });
        });
        return options;
    }, [teamDescList]);

    // 获取阵容描述名称
    const getTeamDescName = (teamDescId) => {
        if (!teamDescId || teamDescId === TEAM_DESC_DEFAULT) return '不修改阵容';
        const team = teamDescList.find(t => t.id === teamDescId);
        return team ? team.name : '已选择';
    };

    // 根据路径获取 teamDescId 值
    const getTeamDescIdByPath = (path) => {
        const keys = path.split(':');
        let value = formValues;
        for (const key of keys) {
            if (value === undefined || value === null) return '';
            value = value[key];
        }
        return value || '';
    };

    const [commandValues] = useState({
        cRecruit: {
            count: 0,
        },
        cBox2001: {
            count: 0,
        },
        cBox2002: {
            count: 0,
        },
        cBox2003: {
            count: 0,
        },
        cBox2004: {
            count: 0,
        },
        cBox2005: {
            count: 0,
        },
    });
    const [isChanged, setIsChanged] = useState(false);

    // 消耗活动兑换配置状态
    const [exchangeActivityConfig, setExchangeActivityConfig] = useState({
        activity: null,
        exchangeList: [],
        taskStartTime: null
    });
    const [loadingExchangeConfig, setLoadingExchangeConfig] = useState(false);

    useEffect(() => {
        setFormValues(fixConfigLimit(JSON.parse(JSON.stringify(user.config))));
        setIsChanged(false);
    }, [user.config, fixConfigLimit]);

    // 获取兑换活动配置
    useEffect(() => {
        const fetchExchangeConfig = async () => {
            setLoadingExchangeConfig(true);
            try {
                const response = await axios.get('/v1/config/exchangeActivity');
                if (response.data.code === 200) {
                    setExchangeActivityConfig(response.data.body);
                } else {
                    console.warn('获取兑换活动配置失败:', response.data.msg);
                }
            } catch (error) {
                console.error('获取兑换活动配置出错:', error);
            } finally {
                setLoadingExchangeConfig(false);
            }
        };

        fetchExchangeConfig();
    }, []);

    // 威震逍遥津兑换配置（多活动分组：玄武兑换/玄武宝库）
    const [weiZhenConfig, setWeiZhenConfig] = useState({ activities: [] });
    const [loadingWeiZhenConfig, setLoadingWeiZhenConfig] = useState(false);
    useEffect(() => {
        const fetchWeiZhenConfig = async () => {
            setLoadingWeiZhenConfig(true);
            try {
                const response = await axios.get('/v1/config/weiZhenExchange');
                if (response.data.code === 200) {
                    setWeiZhenConfig(response.data.body || { activities: [] });
                } else {
                    console.warn('获取威震逍遥津兑换配置失败:', response.data.msg);
                }
            } catch (error) {
                console.error('获取威震逍遥津兑换配置出错:', error);
            } finally {
                setLoadingWeiZhenConfig(false);
            }
        };
        fetchWeiZhenConfig();
    }, []);

    // 消耗活动服务器配置：活动名称、道具名、档位表均从接口推导，免每期手工改代码
    const { act: consumptionAct } = useConsumptionActivity();
    const consumptionMissions = useMemo(() => {
        const byType = {};
        (consumptionAct.missions ?? []).forEach(m => {
            (byType[m.type] = byType[m.type] || []).push(m);
        });
        Object.values(byType).forEach(arr => arr.sort((a, b) => a.id - b.id));
        return byType;
    }, [consumptionAct.missions]);
    // 取某悬赏类型的消耗上限（最高档num），无数据时回退默认值
    const consuMax = (type, fallback) => {
        const arr = consumptionMissions[type];
        return arr?.length ? Math.max(...arr.map(m => m.num)) : fallback;
    };
    // 取某悬赏类型最后N档num，无数据时回退默认档位
    const consuTiers = (type, count, fallback) => {
        const arr = consumptionMissions[type];
        return arr?.length ? arr.slice(-count).map(m => m.num) : fallback;
    };

    // 自动悬赏下一档位试算器：三个输入默认取角色当前实际进度（捕获/金砖取自 actConsumptionStatus，
    // 2级道具总数=仓库+已消耗，与脚本口径一致），可手动修改后调用 /v1/config/consumptionNextGear
    // 实时计算下一档位，用于测试并理解脚本的选档思路，再决定是否打开"自动选择档位"开关
    const [autoGearInput, setAutoGearInput] = useState(null); // { fishing, goldInWan, acquired }
    const [autoGearResult, setAutoGearResult] = useState(null); // 试算接口返回的下一档位信息
    const autoGearReqSeq = useRef(0); // 试算请求序号，丢弃超时后才返回的旧响应，防止旧结果覆盖新结果

    // 角色数据就绪后用当前实际进度初始化输入框（仅初始化一次，之后以用户编辑为准）
    useEffect(() => {
        const status = user.role_info?.actConsumptionStatus;
        if (!autoGearInput && status) {
            setAutoGearInput({
                fishing: status.捕获 ?? 0,
                goldInWan: Math.floor((status.金砖 ?? 0) / 10000),
                acquired: (user.role_info?.items?.[consumptionAct.lv2ItemName] ?? 0) + (status.consumedLv2Items ?? 0),
            });
        }
    }, [autoGearInput, user.role_info, consumptionAct.lv2ItemName]);

    // 输入变化后防抖调用服务端试算接口（计算逻辑在后端，与 autoConsume 同源）
    useEffect(() => {
        if (!autoGearInput) {
            return undefined;
        }

        const seq = ++autoGearReqSeq.current;
        const timer = setTimeout(() => {
            axios.get('/v1/config/consumptionNextGear', { params: {
                fishing: autoGearInput.fishing,
                gold: autoGearInput.goldInWan * 10000,
                acquired: autoGearInput.acquired,
            } }).then(({ data }) => {
                // 只接受最新一次请求的结果，网络乱序时丢弃旧响应
                if (data.code === 200 && seq === autoGearReqSeq.current) {
                    setAutoGearResult(data.body);
                }
            }).catch(() => { /* 试算失败时保留上一次的结果 */ });
        }, 500);
        return () => clearTimeout(timer);
    }, [autoGearInput]);

    const updateAutoGearInput = (field, value) => {
        setAutoGearInput(prev => ({ ...prev, [field]: value ?? 0 }));
    };

    // 试算结果文案
    let autoGearResultText = null;
    if (autoGearResult) {
        if (autoGearResult.gap <= 0) {
            autoGearResultText = `${consumptionAct.lv2ItemName}已达到${consumptionAct.requiredLv2}个，无需自动悬赏`;
        } else if (autoGearResult.fishingTarget === undefined) {
            autoGearResultText = `无下一档：还差${autoGearResult.gap}个${consumptionAct.lv2ItemName}，差距过大或悬赏任务已做满`;
        } else {
            const gearText = `${autoGearResult.fishingTarget}+${autoGearResult.goldTarget / 10000}`;
            const currentText = autoGearResult.currentGearKey === null
                ? '起步档未完成'
                : `当前${autoGearResult.currentGearKey.replace(',', '+')}`;
            autoGearResultText = `下一档位：${gearText}（${currentText}，还差${autoGearResult.gap}个${consumptionAct.lv2ItemName}）`;
        }
    }

    // 获取自选大奖奖励列表
    useEffect(() => {
        axios.get('/v1/config/weekActRewards').then(({ data }) => {
            if (data.code === 200) {
                setWeekActRewards(data.body);
            }
        });
    }, []);

    const onFormChange = (key, values) => {
        if (key === 'study:open') {
            formValues.study.open = values;
        } else if (key === 'team:teamId') {
            formValues.team = formValues.team || { teamId: 0, teamDescId: '' };
            formValues.team.teamId = values;
        } else if (key === 'team:teamDescId') {
            formValues.team = formValues.team || { teamId: 0, teamDescId: '' };
            formValues.team.teamDescId = values;
        } else if (key === 'hangUpTime') {
            formValues.hangUp.hangUpTime = `${values[0].value}:${values[1].value}:${values[2].value}`;
        } else if (key === 'hangUpFriend') {
            formValues.hangUp.friend.open = values;
        } else if (key === 'legion:applyToJoin:open') {
            formValues.legion.applyToJoin.open = values;
        } else if (key === 'legion:applyToJoin:legionNameOrId') {
            formValues.legion.applyToJoin.legionNameOrId = values.trim();
        } else if (key === 'legion:applyToJoin:followRoleId') {
            formValues.legion.applyToJoin.followRoleId = values;
        } else if (key === 'legion:applyToJoin:reason') {
            formValues.legion.applyToJoin.reason = values.trim();
        } else if (key === 'legionSign') {
            formValues.legion.sign.open = values;
        } else if (key === 'legionBossOpen') {
            formValues.legion.boss.open = values;
        } else if (key === 'legionBossCount') {
            formValues.legion.boss.count = values;
        } else if (key === 'legionVote') {
            formValues.legion.vote.legionId = values;
        } else if (key.startsWith('buyGoodsRefresh:')) {
            const strategyKey = key.split(':')[1] || 'default';
            formValues.buyGoods.strategies[strategyKey].refresh = values;
            // 更新该策略下所有商品的最小购买次数不超过最大次数
            Object.keys(formValues.buyGoods.strategies[strategyKey].goods).forEach(goodId => {
                if (formValues.buyGoods.strategies[strategyKey].goods[goodId]?.minBuyRounds > values) {
                    formValues.buyGoods.strategies[strategyKey].goods[goodId].minBuyRounds = values;
                }
            });
        } else if (key.startsWith('buyGoods:')) {
            const parts = key.split(':');
            const strategyKey = parts[1]; // default, 金砖达标, goldenFish
            const goodId = parts[2];
            const subKey = parts[3];
            if (subKey === 'discount') {
                formValues.buyGoods.strategies[strategyKey].goods[goodId].discount = parseFloat(
                    (values / 10).toFixed(2)
                );
            } else if (subKey === 'minBuyRounds') {
                formValues.buyGoods.strategies[strategyKey].goods[goodId].minBuyRounds = values;
            }
        } else if (
            [
                '扫荡灯神',
                '领扫荡券',
                'buyGoods',
                '罐子',
                'hangUp',
                '钓鱼',
                'buyGold',
                'email',
                '领取好友金币',
                '每日咸王',
                '开箱',
                '竞技场',
                'fire',
                'useTorch',
                'dungeon',
                '灯神挑战',
                'secureMode',
                '深海挑战',
                '扫荡深海',
            ].includes(key)
        ) {
            formValues[key].open = values;
        } else if (key === 'tower') {
            formValues.tower.open = values;
        } else if (key === '招募:free') {
            // 免费招募
            formValues.招募.free.open = values;
            if (!values) {
                formValues.招募.twice = false;
            }
        } else if (key === '招募:twice') {
            // 每天2次招募
            formValues.招募.twice = values;
            if (values) {
                // 开启2次招募必定开启免费招募
                formValues.招募.free.open = true;
            }
        } else if (key === '福利') {
            formValues.福利.sign.open = values;
            formValues.福利.每日特惠.open = values;
            formValues.福利.尊享特权.周卡.open = values;
            formValues.福利.尊享特权.月卡.open = values;
            formValues.福利.尊享特权.福利卡.open = values;
            formValues.福利.尊享特权.终身卡.open = values;
        } else if (key === 'email:rm') {
            formValues.email.rm.open = values;
        } else if (key === 'hero:synthesize') {
            formValues.hero.synthesize.open = values;
        } else if (key === 'hero:upgradeStar') {
            formValues.hero.upgradeStar.open = values;
        } else if (key === 'hero:bookReward') {
            formValues.hero.bookReward.open = values;
        } else if (key === 'dailyReward') {
            formValues.dailyReward.open = values;
        } else if (key === '竞技场次数') {
            formValues.竞技场.count = values;
        } else if (key === 'legion:boss:team:teamId') {
            formValues.legion.boss.team = formValues.legion.boss.team || { teamId: 0, teamDescId: '' };
            formValues.legion.boss.team.teamId = values;
        } else if (key === 'legion:boss:team:teamDescId') {
            formValues.legion.boss.team = formValues.legion.boss.team || { teamId: 0, teamDescId: '' };
            formValues.legion.boss.team.teamDescId = values;
        } else if (key === 'tower:team:teamId') {
            formValues.tower.team = formValues.tower.team || { teamId: 0, teamDescId: '' };
            formValues.tower.team.teamId = values;
        } else if (key === 'tower:team:teamDescId') {
            formValues.tower.team = formValues.tower.team || { teamId: 0, teamDescId: '' };
            formValues.tower.team.teamDescId = values;
        } else if (key === 'legion:buyGoods') {
            // 俱乐部商店
            formValues.legion.buyGoods.open = values;
        } else if (key.startsWith('legion:buyGoods:')) {
            const parts = key.split(':');
            const goodsId = parts[2];
            formValues.legion.buyGoods.buyNum[goodsId] = values;
        } else if (key.includes('dungeon')) {
            const arr = key.split(';');
            const shopId = arr[1];
            const itemName = arr[2];
            formValues.dungeon.shop[shopId][itemName] = values;
        } else if (key === '竞技场:team:teamId') {
            formValues.竞技场.team = formValues.竞技场.team || { teamId: 0, teamDescId: '' };
            formValues.竞技场.team.teamId = values;
        } else if (key === '竞技场:team:teamDescId') {
            formValues.竞技场.team = formValues.竞技场.team || { teamId: 0, teamDescId: '' };
            formValues.竞技场.team.teamDescId = values;
        } else if (key === 'arenaTime') {
            formValues.竞技场.time = `${values[0].value}:${values[1].value}:${values[2].value}`;
        } else if (key === 'arenaMode') {
            formValues.竞技场.mode = values[0].value;
        } else if (key === '月度竞技场') {
            if (values) {
                const 咸神门票 = user.role_info.items?.咸神门票 || 0;
                const 咸神积分 = user.role_info.statistics['activity:arena'] ?? 0;
                Dialog.alert({
                    content: `当前竞技场积分为${咸神积分}分，咸神门票为${咸神门票}张。总共需要${240 - 咸神积分}张咸神门票才能保证完成月度竞技场任务。`,
                    confirmText: '确认',
                    // cancelText: '取消',
                    lockScroll: false,
                    hideCancelButton: true,
                    closeOnOverlayClick: true,
                });
            }
            formValues.竞技场.monthly = values;
        } else if (key === '月度钓鱼') {
            if (values) {
                Dialog.alert({
                    content: `当前钓鱼积分为${user.role_info?.statistics?.['month:artifact:2'] || 0}分, 普通鱼竿为${
                        user.role_info.items?.普通鱼竿 || 0
                    }根, 请保证每个月的最后一天留有足够的普通鱼竿`,
                    confirmText: '确认',
                    // cancelText: '取消',
                    lockScroll: false,
                    hideCancelButton: true,
                    closeOnOverlayClick: true,
                });
            }
            formValues.钓鱼.monthly = values;
        } else if (key === '钓鱼:领取金鱼竿') {
            formValues.钓鱼.领取金鱼竿 = values;
        } else if (key === 'buyGoodsTime') {
            formValues.buyGoods.time = `${values[0].value}:${values[1].value}:${values[2].value}`;
        } else if (key === 'war') {
            formValues.war.open = !values;
        } else if (key === 'legion.battleField.signUp.open') {
            formValues.legion.battleField.signUp.open = values;
        } else if (key === 'legion.battleField.signUp.time') {
            formValues.legion.battleField.signUp.time = values.map(option => option.text).join(':');
        } else if (key === '罐子:claim') {
            formValues.罐子.claim = values;
        } else if (key === 'bottleMode') {
            formValues.罐子.claim = values[0].value === 1;
        } else if (key === 'activity:team:teamId') {
            formValues.activity.team = formValues.activity.team || { teamId: 0, teamDescId: '' };
            formValues.activity.team.teamId = values;
        } else if (key === 'activity:team:teamDescId') {
            formValues.activity.team = formValues.activity.team || { teamId: 0, teamDescId: '' };
            formValues.activity.team.teamDescId = values;
        } else if (key === 'activityWelfare') {
            formValues.activity.welfare = formValues.activity.welfare || { open: true };
            formValues.activity.welfare.open = values;
        } else if (key === 'activity:buystoregoods') {
            formValues.activity.buystoregoods.open = values;
        } else if (key.startsWith('activity:buystoregoods:')) {
            // 分割字符串获取店铺ID
            const parts = key.split(':'); // 例如 ["activity", "buystoregoods", "1"]
            const shopId = parseInt(parts[2], 10);
            formValues.activity.buystoregoods.shop[shopId] = values;
        } else if (key === 'activity:box') {
            formValues.activity.box.open = values;
        } else if (key === 'activity:box:rounds') {
            formValues.activity.box.rounds = values;
        } else if (key === 'activity:box:stopStars') {
            formValues.activity.box.stopStars = values;
        } else if (key === 'activity:box:afterWeekday') {
            formValues.activity.box.afterWeekday = values;
        } else if (key.startsWith('activity:box:selectRewards:') ||
            key.startsWith('activity:recruit:selectRewards:') ||
            key.startsWith('activity:gold:selectRewards:')) {
            // 自选大奖配置处理
            const parts = key.split(':');
            const activityType = parts[1]; // box, recruit, gold
            const itemId = parts[3];
            formValues.activity[activityType].selectRewards = formValues.activity[activityType].selectRewards || {};
            if (values > 0) {
                formValues.activity[activityType].selectRewards[itemId] = values;
            } else {
                delete formValues.activity[activityType].selectRewards[itemId];
            }
        } else if (key === 'activity:recruit:selectRewardsOpen') {
            formValues.activity.recruit.selectRewardsOpen = values;
        } else if (key === 'activity:box:selectRewardsOpen') {
            formValues.activity.box.selectRewardsOpen = values;
        } else if (key === 'activity:gacha:free') {
            formValues.activity.gacha = formValues.activity.gacha || {};
            formValues.activity.gacha.free = values;
        } else if (key === 'activity:gold:selectRewardsOpen') {
            formValues.activity.gold.selectRewardsOpen = values;
        } else if (key === 'activity:consumption:open') {
            formValues.activity.consumption.open = values;
        } else if (key === 'activity:consumption:recruitTarget') {
            formValues.activity.consumption.recruitTarget = values;
        } else if (key === 'activity:consumption:boxTarget') {
            formValues.activity.consumption.boxTarget = values;
        } else if (key === 'activity:consumption:fishingTarget') {
            formValues.activity.consumption.fishingTarget = values;
        } else if (key === 'activity:consumption:goldTarget') {
            formValues.activity.consumption.goldTarget = values;
        } else if (key === 'activity:consumption:buyFishingRods') {
            formValues.activity.consumption.buyFishingRods = values;
        } else if (key === 'activity:consumption:openActItems') {
            formValues.activity.consumption.openActItems = values;
        } else if (key === 'activity:consumption:voteForRank') {
            formValues.activity.consumption.voteForRank = values;
        } else if (key === 'activity:consumption:auto') {
            formValues.activity.consumption.auto = values;
        } else if (key === 'activity:consumption:exchange:open') {
            formValues.activity.consumption.exchange.open = values;
        } else if (key === 'activity:consumption:exchange:addTarget') {
            // 添加兑换目标
            if (!formValues.activity.consumption.exchange.targets) {
                formValues.activity.consumption.exchange.targets = [];
            }
            formValues.activity.consumption.exchange.targets.push({
                goodsId: values,
                count: 1,
            });
        } else if (key.startsWith('activity:consumption:exchange:count:')) {
            // 修改兑换次数
            const index = parseInt(key.split(':')[4]);
            if (formValues.activity.consumption.exchange.targets[index]) {
                formValues.activity.consumption.exchange.targets[index].count = values;
            }
        } else if (key.startsWith('activity:consumption:exchange:delete:')) {
            // 删除兑换目标
            const index = parseInt(key.split(':')[4]);
            formValues.activity.consumption.exchange.targets.splice(index, 1);
        } else if (key.startsWith('activity:consumption:exchange:moveUp:')) {
            // 上移
            const index = parseInt(key.split(':')[4]);
            if (index > 0) {
                const temp = formValues.activity.consumption.exchange.targets[index];
                formValues.activity.consumption.exchange.targets[index] =
                    formValues.activity.consumption.exchange.targets[index - 1];
                formValues.activity.consumption.exchange.targets[index - 1] = temp;
            }
        } else if (key.startsWith('activity:consumption:exchange:moveDown:')) {
            // 下移
            const index = parseInt(key.split(':')[4]);
            if (index < formValues.activity.consumption.exchange.targets.length - 1) {
                const temp = formValues.activity.consumption.exchange.targets[index];
                formValues.activity.consumption.exchange.targets[index] =
                    formValues.activity.consumption.exchange.targets[index + 1];
                formValues.activity.consumption.exchange.targets[index + 1] = temp;
            }
        } else if (key === 'activity:weiZhenExchange:open') {
            formValues.activity.weiZhenExchange = formValues.activity.weiZhenExchange || { open: false, targets: [] };
            formValues.activity.weiZhenExchange.open = values;
        } else if (key === 'activity:weiZhenExchange:addTarget') {
            // 添加兑换目标（goodsId 唯一，已存在则不重复添加）
            formValues.activity.weiZhenExchange = formValues.activity.weiZhenExchange || { open: false, targets: [] };
            formValues.activity.weiZhenExchange.targets = formValues.activity.weiZhenExchange.targets || [];
            if (!formValues.activity.weiZhenExchange.targets.some(t => t.goodsId === values)) {
                formValues.activity.weiZhenExchange.targets.push({ goodsId: values, count: 1 });
            }
        } else if (key.startsWith('activity:weiZhenExchange:count:')) {
            const index = parseInt(key.split(':')[3]);
            if (formValues.activity.weiZhenExchange?.targets?.[index]) {
                formValues.activity.weiZhenExchange.targets[index].count = values;
            }
        } else if (key.startsWith('activity:weiZhenExchange:delete:')) {
            const index = parseInt(key.split(':')[3]);
            formValues.activity.weiZhenExchange?.targets?.splice(index, 1);
        } else if (key.startsWith('activity:weiZhenExchange:moveUp:')) {
            const index = parseInt(key.split(':')[3]);
            const targets = formValues.activity.weiZhenExchange?.targets;
            if (targets && index > 0) {
                [targets[index - 1], targets[index]] = [targets[index], targets[index - 1]];
            }
        } else if (key.startsWith('activity:weiZhenExchange:moveDown:')) {
            const index = parseInt(key.split(':')[3]);
            const targets = formValues.activity.weiZhenExchange?.targets;
            if (targets && index < targets.length - 1) {
                [targets[index], targets[index + 1]] = [targets[index + 1], targets[index]];
            }
        } else if (key === 'activity:mine:open') {
            formValues.activity.mine.open = values;
        } else if (key === 'activity:mine:lottery') {
            formValues.activity.mine.lottery = values;
        } else if (key === 'activity:eGame:open') {
            formValues.activity.eGame.open = values;
        } else if (key === 'activity:eGame:lottery') {
            formValues.activity.eGame.lottery = values;
        } else if (key === 'activity:photo:open') {
            formValues.activity.photo = formValues.activity.photo || { open: true };
            formValues.activity.photo.open = values;
        } else if (key === 'activity:multiTower:open') {
            formValues.activity.multiTower.open = values;
        } else if (key === 'activity:charge:open') {
            formValues.activity.charge.open = values;
        } else if (key === 'activity:charge:lottery') {
            formValues.activity.charge.lottery = values;
        } else if (key === 'activity:charge:exchange:open') {
            // 确保exchange对象存在
            if (!formValues.activity.charge.exchange) {
                formValues.activity.charge.exchange = {
                    open: false,
                    targets: []
                };
            }
            formValues.activity.charge.exchange.open = values;
        } else if (key === 'activity:charge:exchange:addTarget') {
            // 添加兑换目标
            if (!formValues.activity.charge.exchange) {
                formValues.activity.charge.exchange = {
                    open: true,
                    targets: []
                };
            }
            if (!formValues.activity.charge.exchange.targets) {
                formValues.activity.charge.exchange.targets = [];
            }
            formValues.activity.charge.exchange.targets.push({
                goodsId: values,
                count: 1,
            });
        } else if (key.startsWith('activity:charge:exchange:count:')) {
            // 修改兑换次数
            const index = parseInt(key.split(':')[4]);
            if (formValues.activity.charge.exchange?.targets?.[index]) {
                formValues.activity.charge.exchange.targets[index].count = values;
            }
        } else if (key.startsWith('activity:charge:exchange:delete:')) {
            // 删除兑换目标
            const index = parseInt(key.split(':')[4]);
            if (formValues.activity.charge.exchange?.targets) {
                formValues.activity.charge.exchange.targets.splice(index, 1);
            }
        } else if (key.startsWith('activity:charge:exchange:moveUp:')) {
            // 上移
            const index = parseInt(key.split(':')[4]);
            if (index > 0 && formValues.activity.charge.exchange?.targets) {
                const temp = formValues.activity.charge.exchange.targets[index];
                formValues.activity.charge.exchange.targets[index] =
                    formValues.activity.charge.exchange.targets[index - 1];
                formValues.activity.charge.exchange.targets[index - 1] = temp;
            }
        } else if (key.startsWith('activity:charge:exchange:moveDown:')) {
            // 下移
            const index = parseInt(key.split(':')[4]);
            if (formValues.activity.charge.exchange?.targets &&
                index < formValues.activity.charge.exchange.targets.length - 1) {
                const temp = formValues.activity.charge.exchange.targets[index];
                formValues.activity.charge.exchange.targets[index] =
                    formValues.activity.charge.exchange.targets[index + 1];
                formValues.activity.charge.exchange.targets[index + 1] = temp;
            }
        } else if (key === '灯神挑战:team:teamId') {
            formValues.灯神挑战.team = formValues.灯神挑战.team || { teamId: 0, teamDescId: '' };
            formValues.灯神挑战.team.teamId = values;
        } else if (key === '灯神挑战:team:teamDescId') {
            formValues.灯神挑战.team = formValues.灯神挑战.team || { teamId: 0, teamDescId: '' };
            formValues.灯神挑战.team.teamDescId = values;
        } else if (key === 'lightState') {
            formValues.灯神挑战.state = values[0].value;
        } else if (key === '深海挑战:team:teamId') {
            formValues.深海挑战.team = formValues.深海挑战.team || { teamId: 0, teamDescId: '' };
            formValues.深海挑战.team.teamId = values;
        } else if (key === '深海挑战:team:teamDescId') {
            formValues.深海挑战.team = formValues.深海挑战.team || { teamId: 0, teamDescId: '' };
            formValues.深海挑战.team.teamDescId = values;
        } else if (key === 'cRecruit') {
            commandValues.cRecruit.count = values;
        } else if (key === 'cBox2001') {
            commandValues.cBox2001.count = values;
        } else if (key === 'cBox2002') {
            commandValues.cBox2002.count = values;
        } else if (key === 'cBox2003') {
            commandValues.cBox2003.count = values;
        } else if (key === 'cBox2004') {
            commandValues.cBox2004.count = values;
        } else if (key === 'cBox2005') {
            commandValues.cBox2005.count = values;
        } else if (key === 'useTorch:木材火把') {
            formValues.useTorch.木材火把 = values;
        } else if (key === 'useTorch:青铜火把') {
            formValues.useTorch.青铜火把 = values;
        } else if (key === 'useTorch:咸神火把') {
            formValues.useTorch.咸神火把 = values;
        } else if (key === 'useTorch:lastTimeInSeconds:hour') {
            const hour = values;
            const minute = Math.floor((formValues.useTorch.lastTimeInSeconds % 3600) / 60);
            formValues.useTorch.lastTimeInSeconds = hour * 3600 + minute * 60;
        } else if (key === 'useTorch:lastTimeInSeconds:minute') {
            const hour = Math.floor(formValues.useTorch.lastTimeInSeconds / 3600);
            const minute = values;
            formValues.useTorch.lastTimeInSeconds = hour * 3600 + minute * 60;
        } else if (key === '每日咸王:team:teamId') {
            formValues.每日咸王.team = formValues.每日咸王.team || { teamId: 0, teamDescId: '' };
            formValues.每日咸王.team.teamId = values;
        } else if (key === '每日咸王:team:teamDescId') {
            formValues.每日咸王.team = formValues.每日咸王.team || { teamId: 0, teamDescId: '' };
            formValues.每日咸王.team.teamDescId = values;
        } else if (key.startsWith('useItems:')) {
            const parts = key.split(':');
            const itemName = parts[1];
            formValues.useItems[itemName] = values;
        } else if (key === 'activity:evoTower:open') {
            formValues.activity.evoTower.open = values;
        } else if (key === 'activity:evoTower:mergeBox') {
            formValues.activity.evoTower.mergeBox = values;
        } else if (key === 'activity:evoTower:team:teamId') {
            formValues.activity.evoTower.team = formValues.activity.evoTower.team || { teamId: 0, teamDescId: '' };
            formValues.activity.evoTower.team.teamId = values;
        } else if (key === 'activity:evoTower:team:teamDescId') {
            formValues.activity.evoTower.team = formValues.activity.evoTower.team || { teamId: 0, teamDescId: '' };
            formValues.activity.evoTower.team.teamDescId = values;
        } else if (key === 'activity:evoTower:legionNameOrId') {
            formValues.activity.evoTower.legionNameOrId = values.trim();
        } else if (key === 'legacy:open') {
            formValues.legacy.open = values;
        } else if (key === 'legacy:hangUp') {
            formValues.legacy.hangUp = values;
        } else if (key === 'legacy:claimGifts') {
            formValues.legacy.claimGifts = values;
        } else if (key === 'legacy:gift:open') {
            formValues.legacy.gift.open = values;
        } else if (key === 'legacy:gift:targetRoleId') {
            formValues.legacy.gift.targetRoleId = values;
        } else if (key === 'legacy:gift:password') {
            formValues.legacy.gift.password = values.trim();
        } else if (key === 'legacy:gift:minQuantity') {
            formValues.legacy.gift.minQuantity = values;
        } else if (key === 'hangUp:feudatory:allowRescue') {
            formValues.hangUp.feudatory.allowRescue = values;
        } else if (key === 'hangUp:feudatory:rescuer') {
            formValues.hangUp.feudatory.rescuer = values.trim();
        }

        setFormValues({ ...formValues });
        const changed = JSON.stringify(formValues) !== JSON.stringify(user.config);
        setIsChanged(changed);
        onConfigChangeCb && onConfigChangeCb(changed ? formValues : null);
        // console.log(key, values)
        // console.log(key, parseFloat((values / 100).toFixed(2)))
    };

    const discountFormatter = value => {
        return `${value}折`;
    };

    // 渲染黑市商品配置Grid的辅助函数
    const renderBuyGoodsGrid = strategyKey => {
        const strategy = formValues.buyGoods.strategies?.[strategyKey];
        if (!strategy) return null;

        const goodsConfig = [
            { id: '1', name: '青铜宝箱', maxDiscount: 5 },
            { id: '2', name: '黄金宝箱', maxDiscount: 8 },
            { id: '3', name: '铂金宝箱', maxDiscount: 8 },
            { id: '4', name: '进阶石', maxDiscount: 10 },
            { id: '5', name: '精铁', maxDiscount: 10 },
            { id: '6', name: '招募令', maxDiscount: 10 },
            { id: '7', name: '随机红将碎片', maxDiscount: 10 },
            { id: '8', name: '随机橙将碎片', maxDiscount: 10 },
            { id: '9', name: '随机紫将碎片', maxDiscount: 10 },
            { id: '10', name: '梦魔晶石', maxDiscount: 10 },
            { id: '11', name: '普通鱼竿', maxDiscount: 8 },
            { id: '12', name: '黄金鱼竿', maxDiscount: 8 },
            { id: '13', name: '咸神门票', maxDiscount: 10 },
            { id: '14', name: '白玉', maxDiscount: 10 },
            { id: '15', name: '彩玉', maxDiscount: 10 },
            { id: '16', name: '扳手', maxDiscount: 10 },
        ];

        return (
            <>
                <Row type='flex' wrap='nowrap' ustify='center' align='center' style={{ paddingBottom: '5px' }}>
                    <Col span='8'>
                        <div className='layout-flex-content'>商品名称</div>
                    </Col>
                    <Col span='8'>
                        <div className='layout-flex-content'>折扣</div>
                    </Col>
                    <Col span='8'>
                        <div className='layout-flex-content'>至少购买次数</div>
                    </Col>
                </Row>
                {goodsConfig.map((goods, index) => (
                    <>
                        <Row
                            key={`row-${strategyKey}-${goods.id}`}
                            className={index % 2 === 1 ? 'odd-row' : ''}
                            type='flex'
                            wrap='nowrap'
                            ustify='center'
                            align='center'
                            style={{ paddingBottom: '5px' }}>
                            <Col span='8'>
                                <div className='layout-flex-content'>{goods.name}</div>
                            </Col>
                            <Col span='8'>
                                <div
                                    className='layout-flex-content'
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}>
                                    <InputNumber
                                        value={Math.trunc((strategy.goods[goods.id]?.discount || 0) * 10)}
                                        onChange={values =>
                                            onFormChange(`buyGoods:${strategyKey}:${goods.id}:discount`, values)
                                        }
                                        max={goods.maxDiscount}
                                        formatter={discountFormatter}
                                        min={0}
                                        step={1}
                                    />
                                </div>
                            </Col>
                            <Col span='8'>
                                <div
                                    className='layout-flex-content'
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}>
                                    <InputNumber
                                        value={strategy.goods[goods.id]?.minBuyRounds || 0}
                                        onChange={values =>
                                            onFormChange(`buyGoods:${strategyKey}:${goods.id}:minBuyRounds`, values)
                                        }
                                        max={strategy.refresh}
                                        min={0}
                                        step={1}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </>
                ))}
            </>
        );
    };

    // 渲染梦境商店商品列表的辅助函数
    const renderDungeonShopItems = merchantLevel => {
        const shopConfig = {
            1: [
                { key: '咸神门票:3', label: '咸神门票X3 | 100万铜币' },
                { key: '咸神火把:1', label: '咸神火把X1 | 100万铜币' },
                { key: '青铜箱子:1', label: '青铜宝箱X1 | 90金砖' },
                { key: '木质宝箱:10', label: '木质宝箱X10 | 100金砖' },
                { key: '精铁:300', label: '精铁X300 | 180金砖' },
                { key: '进阶石:200', label: '进阶石X200 | 200金砖' },
                { key: '普通鱼竿:1', label: '普通鱼竿X1 | 80金砖' },
            ],
            2: [
                { key: '随机橙将碎片:5', label: '随机橙将碎片X5 | 500万铜币' },
                { key: '随机紫将碎片:10', label: '随机紫将碎片X10 | 500万铜币' },
                { key: '黄金鱼竿:5', label: '黄金鱼竿X5 | 2000金砖' },
                { key: '黄金宝箱:5', label: '黄金宝箱X5 | 1500金砖' },
                { key: '梦魇晶石:200', label: '梦魇晶石X200 | 600金砖' },
                { key: '招募令:5', label: '招募令X5 | 1200金砖' },
                { key: '精铁:1500', label: '精铁X1500 | 1000金砖' },
                { key: '进阶石:1000', label: '进阶石X1000 | 1200金砖' },
            ],
            3: [
                { key: '随机红将碎片:10', label: '随机红将碎片X10 | 2500万铜币' },
                { key: '普通鱼竿:10', label: '普通鱼竿X10 | 1000万铜币' },
                { key: '随机橙将碎片:10', label: '随机橙将碎片X10 | 1000万铜币' },
                { key: '黄金鱼竿:10', label: '黄金鱼竿X10 | 3000金砖' },
                { key: '铂金宝箱:5', label: '铂金宝箱X5 | 2000金砖' },
                { key: '梦魇晶石:500', label: '梦魇晶石X500 | 1000金砖' },
                { key: '随机红将碎片:50', label: '随机红将碎片X50 | 5000金砖' },
                { key: '招募令:10', label: '招募令X10 | 2200金砖' },
            ],
        };

        const items = shopConfig[merchantLevel];
        if (!items) return null;

        return items.map((item, index) => (
            <>
                <Row type='flex' wrap='nowrap' justify='center' align='center' style={{ paddingBottom: '5px' }}>
                    <Col
                        span='16'
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}>
                        {item.label}
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.dungeon.shop[merchantLevel][item.key]}
                            onChange={value => onFormChange(`dungeon;${merchantLevel};${item.key}`, value)}
                        />
                    </Col>
                </Row>
                {index < items.length - 1 && <Divider />}
            </>
        ));
    };

    const heroStarFormatter = star => {
        const star2String = [
            '无星',
            '1黄星',
            '2黄星',
            '3黄星',
            '4黄星',
            '5黄星',
            '1紫星',
            '2紫星',
            '3紫星',
            '4紫星',
            '5紫星',
            '1橙星',
            '2橙星',
            '3橙星',
            '4橙星',
            '5橙星',
            '1红星',
            '2红星',
            '3红星',
            '4红星',
            '5红星',
            '1皇冠',
            '2皇冠/1觉',
            '3皇冠',
            '4皇冠',
            '5皇冠/2觉',
            '1紫冠',
            '2紫冠/3觉',
            '3紫冠',
            '4紫冠',
            '5紫冠/4觉',
            '不限制',
        ];
        if (star < star2String.length) {
            return star2String[star];
        }

        return `${star}星`;
    };

    const boxActivityWeekdayOptions = [
        { text: '周六至下周四', value: 6 },
        { text: '周日至下周四', value: 0 },
        { text: '周一至周四', value: 1 },
        { text: '周二至周四', value: 2 },
        { text: '周三至周四', value: 3 },
        { text: '周四', value: 4 },
    ];

    const legionStoreGoods = [
        { id: 1, name: '皮肤币×1', priceItem: '盐锭', price: 2, limit: 5 },
        { id: 2, name: '军团币×5000', priceItem: '盐锭', price: 100, limit: 1 },
        { id: 3, name: '进阶石×8000', priceItem: '盐锭', price: 100, limit: 1 },
        { id: 4, name: '精铁×20000', priceItem: '盐锭', price: 100, limit: 1 },
        { id: 5, name: '白玉×10000', priceItem: '盐锭', price: 100, limit: 1 },
        { id: 6, name: '四圣宝珠碎片×1', priceItem: '盐锭', price: 40, limit: 1 },
        { id: 7, name: '随机红将碎片×100', priceItem: '助威币', price: 20, limit: 1 },
        { id: 8, name: '白玉×2000', priceItem: '助威币', price: 20, limit: 1 },
        { id: 9, name: '军团币×1000', priceItem: '助威币', price: 20, limit: 1 },
        { id: 10, name: '进阶石×1600', priceItem: '助威币', price: 20, limit: 20 },
        { id: 11, name: '精铁×4000', priceItem: '助威币', price: 20, limit: 20 },
    ];

    const useItemsOptions = Object.keys(formValues.useItems).sort((a, b) => a.localeCompare(b, 'zh-CN'));
    return (
        <>
            {/* <NoticeBar content="所有的设置都会有相应的判断,  你招募一次, 我还会招募一次, 你招募两次, 我就不再招募了" /> */}
            {/* <Cell style={cellStyle}>

                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="16" style={{ display: 'flex', alignItems: 'center' }}>
                        安全模式
                        <Ask style={{ marginLeft: '5px' }} onClick={() => {
                            Dialog.alert({
                                content: `日常任务将在0点到4点50中随机执行, 挂机奖励/钓鱼/罐子/爬塔还是定时执行`,
                                confirmText: '确认',
                                // cancelText: '取消',
                                lockScroll: false,
                                hideCancelButton: true,
                                closeOnOverlayClick: true
                            })
                        }} />
                    </Col>
                    <Col span="8" style={{ textAlign: 'right' }}>
                        <Switch style={{ float: 'right' }} checked={formValues.secureMode.open} onChange={value => onFormChange("secureMode", value)} />
                    </Col>
                </Row>
            </Cell> */}

            {/* 快捷导航菜单 */}
            <div
                style={{
                    position: 'sticky',
                    top: '0',
                    backgroundColor: '#fff',
                    zIndex: 100,
                    padding: '10px',
                    borderBottom: '1px solid #eee',
                    marginBottom: '10px',
                }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>快捷导航</h3>
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                    }}>
                    <a href='#section-消耗活动' style={{ padding: '4px 8px', fontSize: '12px', color: '#1989fa', textDecoration: 'none', border: '1px solid #1989fa', borderRadius: '4px' }}>{consumptionAct.name}</a>
                    <a
                        href='#section-挂机设置'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        挂机设置
                    </a>
                    <a
                        href='#section-钓鱼设置'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        钓鱼设置
                    </a>
                    <a
                        href='#section-罐子设置'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        罐子设置
                    </a>
                    <a
                        href='#section-咸鱼大冲关'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        咸鱼大冲关
                    </a>
                    <a
                        href='#section-功法'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        功法
                    </a>
                    <a
                        href='#section-俱乐部'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        俱乐部
                    </a>
                    <a
                        href='#section-咸将塔设置'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        咸将塔设置
                    </a>
                    <a
                        href='#section-咸王设置'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        咸王设置
                    </a>
                    <a
                        href='#section-竞技场'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        竞技场
                    </a>
                    <a
                        href='#section-日常黑市'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        日常黑市
                    </a>
                    <a
                        href='#section-咸王梦境'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        咸王梦境
                    </a>
                    <a
                        href='#section-灯神挑战'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        灯神挑战
                    </a>
                    <a
                        href='#section-深海挑战'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        深海挑战
                    </a>
                    <a
                        href='#section-盐场设置'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        盐场设置
                    </a>
                    <a
                        href='#section-每日任务'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        每日任务
                    </a>
                    <a
                        href='#section-单周活动'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        单周活动
                    </a>
                    <a
                        href='#section-不定期活动'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        不定期活动
                    </a>
                    <a
                        href='#section-预设阵容汇总'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        预设阵容汇总
                    </a>
                    <a
                        href='#section-其他功能'
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: '#1989fa',
                            textDecoration: 'none',
                            border: '1px solid #1989fa',
                            borderRadius: '4px',
                        }}>
                        其他功能
                    </a>
                </div>
            </div>

            {/* 消耗活动设置：不再按活动开闭隐藏，始终展示（未开放时各字段回退默认道具名/档位） */}
            <>
            <h2 id='section-消耗活动' style={h2Style}>
                {consumptionAct.name}
                {/* 新一期已定档但尚未开放：接口已带回该期名称与开始时间，提示几点开放（到点后刷新即切换） */}
                {!consumptionAct.open && consumptionAct.startTime * 1000 > Date.now() && (
                    <span style={{ marginLeft: '6px', fontSize: '12px', fontWeight: 'normal', color: '#fa8c16' }}>
                        （{formatOpenTime(consumptionAct.startTime)}开放）
                    </span>
                )}
            </h2>
            <Cell style={{ flexDirection: 'column' }} >
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="16">消耗活动总开关</Col>
                    <Col span="8" style={{ textAlign: "right" }}>
                        <Switch
                            style={{ float: "right" }}
                            checked={formValues.activity.consumption.open}
                            onChange={value => onFormChange("activity:consumption:open", value)}
                        />
                    </Col>
                </Row>
                { formValues.activity.consumption.open && (<>
                <Divider />
                <Row className="odd-row">
                    <Col span="8">
                        <div className="layout-flex-content">&nbsp;</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">已完成</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">总目标（包含已完成）</div>
                    </Col>
                </Row>
                <Row>
                    <Col span="8">
                        <div className="layout-flex-content">高级捕获</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">{user.role_info?.actConsumptionStatus?.捕获 ?? ''}&nbsp;</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <InputNumber
                                style={{ '--nutui-input-background-color': '#f7f8fa',
                                    '--nutui-inputnumber-input-width': '80px' }}
                                value={formValues.activity.consumption.fishingTarget}
                                allowEmpty={false} min={0} max={consuMax(3, 1750)} step={150}
                                onChange={value => onFormChange("activity:consumption:fishingTarget", value) } />
                        </div>
                    </Col>
                </Row>
                <Row className="odd-row">
                    <Col span="8">
                        <div className="layout-flex-content">招募</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">{user.role_info?.actConsumptionStatus?.招募 ?? ''}&nbsp;</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <InputNumber
                                style={{ '--nutui-input-background-color': '#f7f8fa',
                                    '--nutui-inputnumber-input-width': '80px' }}
                                value={formValues.activity.consumption.recruitTarget}
                                allowEmpty={false} min={0} max={consuMax(1, 4000)} step={200}
                                onChange={value => onFormChange("activity:consumption:recruitTarget", value) } />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col span="8">
                        <div className="layout-flex-content">宝箱</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">{user.role_info?.actConsumptionStatus?.宝箱 ?? ''}&nbsp;</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <InputNumber
                                style={{ '--nutui-input-background-color': '#f7f8fa',
                                    '--nutui-inputnumber-input-width': '80px' }}
                                value={formValues.activity.consumption.boxTarget}
                                allowEmpty={false} min={0} max={consuMax(2, 100000)} step={4000}
                                onChange={value => onFormChange("activity:consumption:boxTarget", value) } />
                        </div>
                    </Col>
                </Row>
                <Row className="odd-row">
                    <Col span="8">
                        <div className="layout-flex-content">金砖</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">{user.role_info?.actConsumptionStatus?.金砖 ?? ''}&nbsp;</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <InputNumber
                                style={{ '--nutui-input-background-color': '#f7f8fa',
                                    '--nutui-inputnumber-input-width': '80px' }}
                                value={formValues.activity.consumption.goldTarget}
                                allowEmpty={false} min={0} max={consuMax(5, 500000)} step={40000}
                                onChange={value => onFormChange("activity:consumption:goldTarget", value) } />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col span="8">
                        <div className="layout-flex-content">盐罐</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">{user.role_info?.actConsumptionStatus?.盐罐 ?? ''}</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content"></div>
                    </Col>
                </Row>
                <Divider />
                <Row>
                    <Col span="8">
                        <div className="layout-flex-content">捕获档位：</div>
                    </Col>
                    <Col span="16">
                        <div className="layout-flex-content">
                        {consuTiers(3, 4, [1300, 1450, 1600, 1750]).map((value) => (
                            <button
                            type="button"
                            key={value}
                            style={{marginLeft: '10px', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit'}}
                            onClick={() => onFormChange("activity:consumption:fishingTarget", value)}>
                            {value}
                            </button>
                        ))}</div>
                    </Col>
                </Row>
                <Divider />
                <Row>
                    <Col span="8">
                        <div className="layout-flex-content">金砖档位（万）：</div>
                    </Col>
                    <Col span="16">
                        <div className="layout-flex-content">
                        {consuTiers(5, 5, [34, 38, 42, 46, 50].map(v => v * 10000)).map((value) => (
                            <button
                            type="button"
                            key={value}
                            style={{marginLeft: '10px', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit'}}
                            onClick={() => onFormChange("activity:consumption:goldTarget", value)}>
                            {value / 10000}
                            </button>
                        ))}</div>
                    </Col>
                </Row>
                <Divider />
                <Row>
                    <Col span="8">
                        <div className="layout-flex-content">{consumptionAct.lv2ItemName}：{user.role_info?.items?.[consumptionAct.lv2ItemName] ?? ''}</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">缺{consumptionAct.lv2ItemName}：{consumptionAct.requiredLv2 - (user.role_info?.items?.[consumptionAct.lv2ItemName] ?? 0)}</div>
                    </Col>
                    <Col span="8">
                        <div className="layout-flex-content">缺{consumptionAct.lv1ItemName}：{(consumptionAct.requiredLv2 - (user.role_info?.items?.[consumptionAct.lv2ItemName] ?? 0)) * 4}</div>
                    </Col>
                </Row>
                <Divider />
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="16">金鱼竿不足时按原价购买（600金砖/根）</Col>
                    <Col span="8" style={{ textAlign: "right" }}>
                        <Switch
                            style={{ float: "right" }}
                            checked={formValues.activity.consumption.buyFishingRods}
                            onChange={value => onFormChange("activity:consumption:buyFishingRods", value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="16">使用{consumptionAct.lv1ItemName}</Col>
                    <Col span="8" style={{ textAlign: "right" }}>
                        <Switch
                            style={{ float: "right" }}
                            checked={formValues.activity.consumption.openActItems}
                            onChange={value => onFormChange("activity:consumption:openActItems", value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="16">{consumptionAct.rankName}</Col>
                    <Col span="8" style={{ textAlign: "right" }}>
                        <Switch
                            style={{ float: "right" }}
                            checked={formValues.activity.consumption.voteForRank}
                            onChange={value => onFormChange("activity:consumption:voteForRank", value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="16">自动选择档位</Col>
                    <Col span="8" style={{ textAlign: "right" }}>
                        <Switch
                            style={{ float: "right" }}
                            checked={formValues.activity.consumption.auto}
                            onChange={value => onFormChange("activity:consumption:auto", value)}
                        />
                    </Col>
                </Row>
                {autoGearInput && (
                    <>
                        <Divider />
                        <Row>
                            <Col span="24">
                                <div>下一档位试算（修改任意数值，查看脚本在各种情况下判断的下一档位）：</div>
                                <Row type="flex" align="center" style={{ marginTop: '8px' }}>
                                    <Col span="8">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            钓鱼
                                            <InputNumber
                                                style={{ '--nutui-input-background-color': '#f7f8fa',
                                                    '--nutui-inputnumber-input-width': '70px' }}
                                                value={autoGearInput.fishing}
                                                allowEmpty={false} min={0} step={50}
                                                onChange={value => updateAutoGearInput('fishing', value)} />
                                        </div>
                                    </Col>
                                    <Col span="8">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            金砖(万)
                                            <InputNumber
                                                style={{ '--nutui-input-background-color': '#f7f8fa',
                                                    '--nutui-inputnumber-input-width': '60px' }}
                                                value={autoGearInput.goldInWan}
                                                allowEmpty={false} min={0} step={4}
                                                onChange={value => updateAutoGearInput('goldInWan', value)} />
                                        </div>
                                    </Col>
                                    <Col span="8">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            {consumptionAct.lv2ItemName}
                                            <InputNumber
                                                style={{ '--nutui-input-background-color': '#f7f8fa',
                                                    '--nutui-inputnumber-input-width': '60px' }}
                                                value={autoGearInput.acquired}
                                                allowEmpty={false} min={0} step={1}
                                                onChange={value => updateAutoGearInput('acquired', value)} />
                                        </div>
                                    </Col>
                                </Row>
                                {autoGearResultText && (
                                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#1989fa' }}>
                                        {autoGearResultText}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </>
                )}
                <Divider />
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="16">兑换奖励道具{exchangeActivityConfig.taskStartTime ? `（${new Date(exchangeActivityConfig.taskStartTime * 1000).toLocaleString('zh-CN', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}开放）` : ''}</Col>
                    <Col span="8" style={{ textAlign: "right" }}>
                        <Switch
                            style={{ float: "right" }}
                            checked={formValues.activity.consumption.exchange?.open}
                            onChange={value => onFormChange("activity:consumption:exchange:open", value)}
                        />
                    </Col>
                </Row>
                {formValues.activity.consumption.exchange?.open && (
                    <>
                        <Divider />
                        <Row>
                            <Col span="12">
                                现有金鱼：{user.role_info?.goldenFishes?.length > 0 ? user.role_info.goldenFishes.join(', ') : '暂无金色鱼灵'}
                            </Col>
                            <Col span="12">
                                现有{exchangeActivityConfig.activity?.itemName ?? '活动道具'}：{user.role_info?.items?.[exchangeActivityConfig.activity?.itemName ?? '活动道具'] ?? '0'}
                            </Col>
                        </Row>
                        <Divider />
                        <Row>
                            <Col span="24">
                                <div>兑换目标列表（按由上到下顺序，道具不足时立即终止兑换）：</div>
                                <Row type="flex" align="center">
                                    <Col span="10">道具名称</Col>
                                    <Col span="7">累计兑换次数</Col>
                                    <Col span="7">调整顺序</Col>
                                </Row>
                                {formValues.activity.consumption.exchange?.targets?.map((target, index) => {
                                    const conf = exchangeActivityConfig.exchangeList.find(c => c.id === target.goodsId);
                                    if (!conf) return null;
                                    return (
                                        <div key={index} style={{
                                            border: '1px solid #eee',
                                            borderRadius: '8px',
                                            padding: '10px',
                                            marginBottom: '10px',
                                            backgroundColor: '#f9f9f9'
                                        }}>
                                            <Row type="flex" align="center">
                                                <Col span="10">
                                                    <div style={{ fontWeight: 'bold' }}>
                                                        {conf.rewardName}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                                        需要{conf.consumeNum}个{exchangeActivityConfig.activity?.itemName ?? '活动道具'} | 限{conf.limitNum}次
                                                        {conf.unlockNeed > 0 && `（累计消耗${conf.unlockNeed}个${exchangeActivityConfig.activity?.itemName ?? '活动道具'}解锁）`}
                                                    </div>
                                                </Col>
                                                <Col span="7">
                                                <InputNumber
                                                    value={target.count}
                                                    min={1}
                                                    max={conf.limitNum}
                                                    onChange={value => onFormChange(`activity:consumption:exchange:count:${index}`, parseInt(value) || 1)}
                                                    style={{ width: '100px', marginRight: '5px' }}
                                                />
                                                </Col>
                                                <Col span="7">
                                                <Button
                                                    type="default"
                                                    size="mini"
                                                    disabled={index === 0}
                                                    onClick={() => onFormChange(`activity:consumption:exchange:moveUp:${index}`, null)}
                                                    style={{ marginRight: '5px' }}
                                                >
                                                    ↑
                                                </Button>
                                                <Button
                                                    type="default"
                                                    size="mini"
                                                    disabled={index === formValues.activity.consumption.exchange.targets.length - 1}
                                                    onClick={() => onFormChange(`activity:consumption:exchange:moveDown:${index}`, null)}
                                                    style={{ marginRight: '5px' }}
                                                >
                                                    ↓
                                                </Button>
                                                <Button
                                                    type="danger"
                                                    size="mini"
                                                    onClick={() => onFormChange(`activity:consumption:exchange:delete:${index}`, null)}
                                                >
                                                    X
                                                </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    );
                                })}
                                <Button
                                    type="info"
                                    size="normal"
                                    block
                                    onClick={() => setVisible({ exchangeTargetPicker: true })}
                                    style={{ marginTop: '10px' }}
                                >
                                    + 添加兑换目标
                                </Button>
                            </Col>
                        </Row>
                        <Popup
                            closeable
                            title="选择兑换道具"
                            visible={visible.exchangeTargetPicker}
                            style={{ height: "60%" }}
                            position="bottom"
                            onClose={() => setVisible({ exchangeTargetPicker: false })}>
                            <div style={{
                                padding: '10px',
                                maxHeight: 'calc(60vh - 60px)',
                                overflowY: 'auto'
                            }}>
                                {loadingExchangeConfig ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        加载中...
                                    </div>
                                ) : exchangeActivityConfig.exchangeList.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                        暂无兑换配置
                                    </div>
                                ) : (
                                    exchangeActivityConfig.exchangeList.map(conf => {
                                    // 检查是否已拥有该金鱼
                                    const goldenFishes = user.role_info?.goldenFishes || [];
                                    const isOwned = goldenFishes.some(fish => {
                                        // 提取鱼名（去除数量标记，如"骇浪5×1"取"骇浪5"）
                                        const fishName = fish.split('×')[0];
                                        const rewardItemName = conf.rewardName.split('×')[0];
                                        return fishName === rewardItemName;
                                    });

                                    return (
                                        <div
                                            key={conf.rewardId}
                                            style={{
                                                marginBottom: '10px',
                                                padding: '12px',
                                                border: '1px solid #e8e8e8',
                                                borderRadius: '8px',
                                                backgroundColor: isOwned ? '#f0f0f0' : '#fafafa',
                                                opacity: isOwned ? 0.6 : 1,
                                                cursor: isOwned ? 'not-allowed' : 'pointer'
                                            }}
                                            onClick={() => {
                                                if (!isOwned) {
                                                    setVisible({ exchangeTargetPicker: false });
                                                    onFormChange("activity:consumption:exchange:addTarget", parseInt(conf.id));
                                                }
                                            }}>
                                            <div style={{ fontSize: '14px' }}>
                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                    {conf.rewardName}
                                                    {isOwned && <span style={{ color: '#999', marginLeft: '8px' }}>(已拥有)</span>}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#666' }}>
                                                    需要{conf.consumeNum}个{exchangeActivityConfig.activity?.itemName ?? '活动道具'} | 限{conf.limitNum}次
                                                    {conf.unlockNeed > 0 && `（累计消耗${conf.unlockNeed}个${exchangeActivityConfig.activity?.itemName ?? '活动道具'}解锁）`}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                                )}
                            </div>
                        </Popup>
                    </>
                )}</>)}
            </Cell>
            </>

            <h2 id='section-挂机设置' style={h2Style}>
                挂机设置
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>领取间隔</Col>
                    <Col span='8' style={{ textAlign: 'right' }} onClick={() => setVisible({ hangUpTime: true })}>
                        <div>{formValues.hangUp.hangUpTime} </div>
                        {/* <ArrowRight /> */}
                    </Col>
                    <DatePicker
                        title='选择挂机时间'
                        type='time'
                        defaultValue={new Date(`2025-05-01 ${formValues.hangUp.hangUpTime}`)}
                        visible={visible.hangUpTime}
                        onClose={() => setVisible({ hangUpTime: false })}
                        onConfirm={values => onFormChange('hangUpTime', values)}
                        filter={(type, options) => {
                            if (type === 'hour') {
                                return options.filter(option => option.value <= 12);
                            }
                            return options;
                        }}
                    />
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>定时领取</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.hangUp.open}
                            onChange={value => onFormChange('hangUp', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>免费加钟</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.hangUp.friend.open}
                            onChange={value => onFormChange('hangUpFriend', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>领取火把</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.fire.open}
                            onChange={value => onFormChange('fire', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>使用火把</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.useTorch.open}
                            onChange={value => onFormChange('useTorch', value)}
                        />
                    </Col>
                </Row>
                {formValues.useTorch.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16'>使用火把设置</Col>
                            <Col span='8' style={{ textAlign: 'right' }}>
                                <Button type='info' size='mini' onClick={() => setVisible({ useTorch: true })}>
                                    设置
                                </Button>
                            </Col>
                            <Popup
                                closeable
                                title='使用火把设置'
                                visible={visible.useTorch}
                                style={{ height: '40%' }}
                                position='bottom'
                                onClose={() => setVisible({ useTorch: false })}>
                                <Cell style={cellStyle}>
                                    <Row type='flex' wrap='nowrap' justify='center' align='center'>
                                        <Col
                                            span='24'
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                flexDirection: 'row',
                                            }}>
                                            <Ask
                                                style={{ marginLeft: '5px' }}
                                                onClick={() => {
                                                    Dialog.alert({
                                                        content: (
                                                            <>
                                                                <p>
                                                                    自动使用指定类型的火把，并保持火把的剩余时间不少于“持续时间”。火把会按照：木材火把、青铜火把、咸神火把的顺序依次使用，当前一种火把的时间耗尽且火把也耗尽后才会使用下一种火把。如果当前生效的火把时间还没有耗尽，并且启用了该种类型的火把，那么会继续使用当前火把。不会用另一种类型的火把覆盖当前生效的火把。
                                                                </p>
                                                            </>
                                                        ),
                                                        confirmText: '确认',
                                                        lockScroll: false,
                                                        hideCancelButton: true,
                                                        closeOnOverlayClick: true,
                                                    });
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row type='flex' wrap='nowrap' justify='center' align='center'>
                                        <Col span='10'>持续时间</Col>
                                        <Col
                                            span='14'
                                            style={{
                                                textAlign: 'right',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                flexWrap: 'nowrap',
                                                justifyContent: 'flex-end',
                                            }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <InputNumber
                                                    style={{
                                                        '--nutui-input-background-color': '#f7f8fa',
                                                        '--nutui-inputnumber-input-width': '50px',
                                                    }}
                                                    value={Math.floor(formValues.useTorch.lastTimeInSeconds / 3600)}
                                                    allowEmpty={false}
                                                    min={0}
                                                    max={23}
                                                    onChange={value =>
                                                        onFormChange('useTorch:lastTimeInSeconds:hour', value)
                                                    }
                                                />
                                                <span>小时</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <InputNumber
                                                    style={{
                                                        '--nutui-input-background-color': '#f7f8fa',
                                                        '--nutui-inputnumber-input-width': '50px',
                                                    }}
                                                    value={Math.floor(
                                                        (formValues.useTorch.lastTimeInSeconds % 3600) / 60
                                                    )}
                                                    allowEmpty={false}
                                                    min={0}
                                                    max={59}
                                                    onChange={value =>
                                                        onFormChange('useTorch:lastTimeInSeconds:minute', value)
                                                    }
                                                />
                                                <span>分</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row type='flex' wrap='nowrap' justify='center' align='center'>
                                        <Col span='10'>木材火把</Col>
                                        <Col span='14' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.useTorch.木材火把}
                                                onChange={value => onFormChange('useTorch:木材火把', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row type='flex' wrap='nowrap' justify='center' align='center'>
                                        <Col span='10'>青铜火把</Col>
                                        <Col span='14' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.useTorch.青铜火把}
                                                onChange={value => onFormChange('useTorch:青铜火把', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row type='flex' wrap='nowrap' justify='center' align='center'>
                                        <Col span='10'>咸神火把</Col>
                                        <Col span='14' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.useTorch.咸神火把}
                                                onChange={value => onFormChange('useTorch:咸神火把', value)}
                                            />
                                        </Col>
                                    </Row>
                                </Cell>
                            </Popup>
                        </Row>
                    </>
                )}
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        允许其他角色让{user.role_info?.name || user.name}解救
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: (
                                        <>
                                            <p>开启后，当其他角色被咸主抓走时，可以让{user.role_info?.name}来解救。{user.role_info?.name}会先征服被抓角色（击败原咸主成为新咸主），然后赦免该角色。</p>
                                            <p>开启后不受自动上号总开关的影响。</p>
                                        </>
                                    ),
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.hangUp?.feudatory?.allowRescue}
                            onChange={value => onFormChange('hangUp:feudatory:allowRescue', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='12' style={{ display: 'flex', alignItems: 'center' }}>
                        被抓时的解救人
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `填写解救人的卡密。在领取主线挂机奖励前如果本角色被咸主抓走，会先让指定的解救人来解救自己。解救人需要开启"允许其他角色让自己解救"功能。`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='12' style={{ textAlign: 'right' }}>
                        <Input
                            placeholder='填写解救人的卡密'
                            value={formValues.hangUp?.feudatory?.rescuer || ''}
                            onChange={value => onFormChange('hangUp:feudatory:rescuer', value)}
                            style={{ textAlign: 'right', '--nutui-input-background-color': '#f7f8fa', '--nutui-input-padding': '5px 5px' }}
                            clearable
                            maxLength={42}
                            align={'center'}
                        />
                    </Col>
                </Row>
            </Cell>
            <h2 id='section-钓鱼设置' style={h2Style}>
                钓鱼设置
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>免费钓鱼</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.钓鱼.open}
                            onChange={value => onFormChange('钓鱼', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='18' style={{ display: 'flex', alignItems: 'center' }}>
                        领取金鱼竿
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `自动领取普通捕获满20次奖励的金鱼竿`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='6' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.钓鱼.领取金鱼竿}
                            onChange={value => onFormChange('钓鱼:领取金鱼竿', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='18' style={{ display: 'flex', alignItems: 'center' }}>
                        月度钓鱼
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `每个月的最后一天将会执行月度钓鱼任务, 请保证有足够的普通鱼竿`,
                                    confirmText: '确认',
                                    // cancelText: '取消',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='6' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.钓鱼.monthly}
                            onChange={value => onFormChange('月度钓鱼', value)}
                        />
                    </Col>
                </Row>
            </Cell>
            <h2 id='section-罐子设置' style={h2Style}>
                罐子设置
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='18' style={{ display: 'flex', alignItems: 'center' }}>
                        开启罐子机器人
                    </Col>
                    <Col span='6' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.罐子.open}
                            onChange={value => onFormChange('罐子', value)}
                        />
                    </Col>
                </Row>
                {formValues.罐子.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col
                                span='18'
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                罐子模式
                                <Ask
                                    style={{ marginLeft: '5px' }}
                                    onClick={() => {
                                        Dialog.alert({
                                            content: (
                                                <>
                                                    <p>{`囤罐子: 极限囤罐子, 每天只收一个罐子用来做日活跃, 其他全部囤起来, 优先级顺序是 铜>银>金, 只有在好友里面没有铜的时候才会收银或者金`}</p>
                                                    <p>收罐子: 每次上线如果有罐子就会领</p>
                                                </>
                                            ),
                                            confirmText: '确认',
                                            // cancelText: '取消',
                                            lockScroll: false,
                                            hideCancelButton: true,
                                            closeOnOverlayClick: true,
                                        });
                                    }}
                                />
                            </Col>
                            <Col
                                span='14'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ bottleMode: true })}>
                                <div>
                                    {formValues.罐子.claim ? bottleModeOptions[1].text : bottleModeOptions[0].text}{' '}
                                </div>
                            </Col>
                            <Picker
                                title='罐子模式'
                                visible={visible.bottleMode}
                                options={bottleModeOptions}
                                value={[
                                    formValues.罐子.claim ? bottleModeOptions[1].value : bottleModeOptions[0].value,
                                ]}
                                onConfirm={values => onFormChange('bottleMode', values)}
                                onClose={() => setVisible({ bottleMode: false })}
                            />
                        </Row>
                    </>
                )}
            </Cell>
            <h2 id='section-咸鱼大冲关' style={h2Style}>
                咸鱼大冲关
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='18' style={{ display: 'flex', alignItems: 'center' }}>
                        开启答题
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: (
                                        <>
                                            <p>
                                                开启后每周自动答题，如果没有全部答对，则下次上号时会再次答题，直至领取完全部答题奖励。
                                            </p>
                                        </>
                                    ),
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='6' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.study.open}
                            onChange={value => onFormChange('study:open', value)}
                        />
                    </Col>
                </Row>
            </Cell>
            <h2 id='section-功法' style={h2Style}>
                功法
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>功法功能</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.legacy.open}
                            onChange={value => onFormChange('legacy:open', value)}
                        />
                    </Col>
                </Row>
                {formValues.legacy.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16'>领取挂机奖励</Col>
                            <Col span='8' style={{ textAlign: 'right' }}>
                                <Switch
                                    style={{ float: 'right' }}
                                    checked={formValues.legacy.hangUp}
                                    onChange={value => onFormChange('legacy:hangUp', value)}
                                />
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16'>领取功法残卷礼物</Col>
                            <Col span='8' style={{ textAlign: 'right' }}>
                                <Switch
                                    style={{ float: 'right' }}
                                    checked={formValues.legacy.claimGifts}
                                    onChange={value => onFormChange('legacy:claimGifts', value)}
                                />
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16'>赠送功法残卷</Col>
                            <Col span='8' style={{ textAlign: 'right' }}>
                                <Switch
                                    style={{ float: 'right' }}
                                    checked={formValues.legacy.gift.open}
                                    onChange={value => onFormChange('legacy:gift:open', value)}
                                />
                            </Col>
                        </Row>
                        {formValues.legacy.gift.open && (
                            <>
                                <Row type='flex' wrap='nowrap' justify='end' align='center'>
                                    <Col span='10' style={{ textAlign: 'right' }}>
                                        <div className='layout-flex-content'>接收角色ID：</div>
                                    </Col>
                                    <Col span='10' style={{ textAlign: 'right' }}>
                                        <InputNumber
                                            style={{
                                                '--nutui-input-background-color': '#f7f8fa',
                                                '--nutui-inputnumber-input-width': '80px',
                                            }}
                                            placeholder='角色ID'
                                            value={formValues.legacy.gift.targetRoleId}
                                            allowEmpty={false}
                                            min={0}
                                            max={9999999999}
                                            onChange={value => onFormChange('legacy:gift:targetRoleId', value)}
                                        />
                                    </Col>
                                </Row>
                                <Row type='flex' wrap='nowrap' justify='end' align='center'>
                                    <Col span='10' style={{ textAlign: 'right' }}>
                                        <div className='layout-flex-content'>安全锁密码：</div>
                                    </Col>
                                    <Col span='10' style={{ textAlign: 'right' }}>
                                        <Input
                                            type='password'
                                            style={{
                                                '--nutui-input-background-color': '#f7f8fa',
                                                '--nutui-input-padding': '5px 5px',
                                            }}
                                            placeholder='密码'
                                            clearable
                                            value={formValues.legacy.gift.password}
                                            maxLength={20}
                                            align={'center'}
                                            onChange={value => onFormChange('legacy:gift:password', value)}
                                        />
                                    </Col>
                                </Row>
                                <Row type='flex' wrap='nowrap' justify='end' align='center'>
                                    <Col span='10' style={{ textAlign: 'right' }}>
                                        <div className='layout-flex-content'>
                                            最小赠送数量：
                                            <Ask
                                                style={{ marginLeft: '5px' }}
                                                onClick={() => {
                                                    Dialog.alert({
                                                        content: (
                                                            <>
                                                                <p>
                                                                    如果功法残卷数量不足该数量，则暂不赠送。设置合适的数量可以减少接收人邮件数量超过上限的情况。
                                                                </p>
                                                            </>
                                                        ),
                                                        confirmText: '确认',
                                                        lockScroll: false,
                                                        hideCancelButton: true,
                                                        closeOnOverlayClick: true,
                                                    });
                                                }}
                                            />
                                        </div>
                                    </Col>
                                    <Col span='10' style={{ textAlign: 'right' }}>
                                        <InputNumber
                                            style={{
                                                '--nutui-input-background-color': '#f7f8fa',
                                                '--nutui-inputnumber-input-width': '80px',
                                            }}
                                            placeholder='阈值'
                                            value={formValues.legacy.gift.minQuantity}
                                            allowEmpty={false}
                                            min={0}
                                            max={999999}
                                            onChange={value => onFormChange('legacy:gift:minQuantity', value)}
                                        />
                                    </Col>
                                </Row>
                            </>
                        )}
                    </>
                )}
            </Cell>
            <h2 id='section-俱乐部' style={h2Style}>
                俱乐部
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>
                        申请加入
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: (
                                        <>
                                            <p>
                                                如果不在俱乐部中，则申请加入指定俱乐部。如果已在任意俱乐部中则不进行任何操作。
                                            </p>
                                            <p>
                                                “俱乐部”可以填写俱乐部名称或俱乐部ID。如果不加入指定俱乐部，则保留空白。
                                            </p>
                                            <p>“跟随角色ID”可以申请加入指定角色ID所在的俱乐部。填0则不跟随角色。</p>
                                            <p>如果以上两者都填写了且不是同一个俱乐部，则同时申请加入这两个俱乐部。</p>
                                            <p>
                                                “申请理由”请提前在游戏中进行测试，否则可能因为包含非法字符而申请失败。
                                            </p>
                                        </>
                                    ),
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.legion.applyToJoin.open}
                            onChange={value => onFormChange('legion:applyToJoin:open', value)}
                        />
                    </Col>
                </Row>
                <Row type='flex' wrap='nowrap' justify='end' align='center'>
                    <Col span='10' style={{ textAlign: 'right' }}>
                        <div className='layout-flex-content'>俱乐部：</div>
                    </Col>
                    <Col span='10' style={{ textAlign: 'right' }}>
                        <Input
                            disabled={!formValues.legion.applyToJoin.open}
                            style={{ '--nutui-input-background-color': '#f7f8fa', '--nutui-input-padding': '5px 5px' }}
                            placeholder='名称或ID'
                            clearable
                            value={formValues.legion.applyToJoin.legionNameOrId}
                            maxLength={10}
                            align={'center'}
                            onChange={value => onFormChange('legion:applyToJoin:legionNameOrId', value)}
                        />
                    </Col>
                </Row>
                <Row type='flex' wrap='nowrap' justify='end' align='center'>
                    <Col span='10' style={{ textAlign: 'right' }}>
                        <div className='layout-flex-content'>跟随角色ID：</div>
                    </Col>
                    <Col span='10' style={{ textAlign: 'right' }}>
                        <InputNumber
                            disabled={!formValues.legion.applyToJoin.open}
                            style={{
                                '--nutui-input-background-color': '#f7f8fa',
                                '--nutui-inputnumber-input-width': '80px',
                            }}
                            placeholder='角色ID'
                            value={formValues.legion.applyToJoin.followRoleId}
                            allowEmpty={false}
                            min={0}
                            max={9999999999}
                            onChange={value => onFormChange('legion:applyToJoin:followRoleId', value)}
                        />
                    </Col>
                </Row>
                <Row type='flex' wrap='nowrap' justify='end' align='center'>
                    <Col span='10' style={{ textAlign: 'right' }}>
                        <div className='layout-flex-content'>申请理由：</div>
                    </Col>
                    <Col span='10' style={{ textAlign: 'right' }}>
                        <Input
                            disabled={!formValues.legion.applyToJoin.open}
                            style={{ '--nutui-input-background-color': '#f7f8fa', '--nutui-input-padding': '5px 5px' }}
                            placeholder='理由'
                            clearable
                            value={formValues.legion.applyToJoin.reason}
                            maxLength={42}
                            align={'center'}
                            onChange={value => onFormChange('legion:applyToJoin:reason', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>俱乐部签到</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.legion.sign.open}
                            onChange={value => onFormChange('legionSign', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        俱乐部商店
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.legion.buyGoods.open}
                            onChange={value => onFormChange('legion:buyGoods', value)}
                        />
                    </Col>
                </Row>
                {formValues.legion.buyGoods.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                                俱乐部商店设置
                            </Col>
                            <Col span='8' style={{ textAlign: 'right' }}>
                                <Button type='info' size='mini' onClick={() => setVisible({ legionBuyGoods: true })}>
                                    设置
                                </Button>
                            </Col>
                            <Popup
                                closeable
                                title='俱乐部商店'
                                visible={visible.legionBuyGoods}
                                style={{ height: '70%' }}
                                position='bottom'
                                onClose={() => setVisible({ legionBuyGoods: false })}>
                                <Row
                                    type='flex'
                                    wrap='nowrap'
                                    justify='center'
                                    align='center'
                                    style={{ paddingBottom: '10px' }}>
                                    <Col span='10' style={{ display: 'flex', alignItems: 'center' }}>
                                        商品名称
                                    </Col>
                                    <Col span='6' style={{ textAlign: 'center' }}>
                                        售价
                                    </Col>
                                    <Col span='8' style={{ textAlign: 'center' }}>
                                        购买次数
                                    </Col>
                                </Row>
                                <Divider />
                                {legionStoreGoods.map(good => (
                                    <>
                                        <Row
                                            key={good.id}
                                            type='flex'
                                            wrap='nowrap'
                                            justify='center'
                                            align='center'
                                            style={{ paddingBottom: '10px' }}>
                                            <Col span='10' style={{ display: 'flex', alignItems: 'center' }}>
                                                {good.name}
                                            </Col>
                                            <Col span='6' style={{ textAlign: 'center' }}>
                                                {good.price}
                                                {good.priceItem}
                                            </Col>
                                            <Col
                                                span='8'
                                                style={{
                                                    textAlign: 'center',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}>
                                                <InputNumber
                                                    value={formValues.legion.buyGoods.buyNum[good.id]}
                                                    min={0}
                                                    max={good.limit}
                                                    onChange={value =>
                                                        onFormChange(`legion:buyGoods:${good.id}`, value)
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                        <Divider />
                                    </>
                                ))}
                            </Popup>
                        </Row>
                    </>
                )}
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>俱乐部BOSS开关</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            checked={formValues.legion.boss.open}
                            onChange={checked => onFormChange('legionBossOpen', checked)}
                        />
                    </Col>
                </Row>
                {formValues.legion.boss.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'> </Col>
                            <Col span='7'>挑战次数</Col>
                            <Col span='16'>
                                <Radio.Group
                                    value={formValues.legion.boss.count}
                                    shape='button'
                                    onChange={e => onFormChange('legionBossCount', e)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {legionBossOptions.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='7'>预设阵容</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.legion.boss.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('legion:boss:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: 'legion:boss:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.legion.boss.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
                {/* <Divider />
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="16">年赛助威</Col>
                    <Col
                        span="14"
                        style={{ textAlign: "right" }}
                        onClick={() => setVisible({ legionVote: true })}>
                        <div>
                            {
                                legionVoteOptions.find(
                                    item =>
                                        item.value === formValues.legion.vote.legionId
                                ).text
                            }{" "}
                        </div>
                    </Col>
                    <Picker
                        title="年赛助威列表"
                        visible={visible.legionVote}
                        options={legionVoteOptions}
                        value={[
                            legionVoteOptions.find(
                                item =>
                                    item.value === formValues.legion.vote.legionId
                            ).value,
                        ]}
                        onConfirm={values => onFormChange("legionVote", values[0].value)}
                        onClose={() => setVisible({ legionVote: false })}
                    />
                </Row> */}
            </Cell>

            <h2 id='section-咸将塔设置' style={h2Style}>
                咸将塔设置
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8' style={{ display: 'flex', alignItems: 'center' }}>
                        咸将塔
                    </Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.tower.open}
                            onChange={value => onFormChange('tower', value)}
                        />
                    </Col>
                </Row>
                {formValues.tower.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>预设阵容</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.tower.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('tower:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: 'tower:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.tower.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
            </Cell>

            <h2 id='section-咸王设置' style={h2Style}>
                咸王设置
            </h2>
            <Cell style={cellStyle}>
                {/* <Divider /> */}
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8'>每日咸王</Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.每日咸王.open}
                            onChange={value => onFormChange('每日咸王', value)}
                        />
                    </Col>
                </Row>
                {formValues.每日咸王.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>预设阵容</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.每日咸王.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('每日咸王:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: '每日咸王:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.每日咸王.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
            </Cell>


            <h2 id='section-竞技场' style={h2Style}>
                竞技场
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>挑战竞技场</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.竞技场.open}
                            onChange={value => onFormChange('竞技场', value)}
                        />
                    </Col>
                </Row>

                {formValues.竞技场.open && (
                    <>
                        {!formValues.secureMode.open && (
                            <>
                                <Divider />
                                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                                    <Col
                                        span='16'
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}>
                                        挑战时间
                                        <Ask
                                            style={{ marginLeft: '5px' }}
                                            onClick={() => {
                                                Dialog.alert({
                                                    content:
                                                        '上号时间会有一定的延迟, 至少提前10分钟, 否则可能无法正常做完任务',
                                                    confirmText: '确认',
                                                    // cancelText: '取消',
                                                    lockScroll: false,
                                                    hideCancelButton: true,
                                                    closeOnOverlayClick: true,
                                                });
                                            }}
                                        />
                                    </Col>

                                    <Col
                                        span='8'
                                        style={{ textAlign: 'right' }}
                                        onClick={() => setVisible({ arenaTime: true })}>
                                        <div>{formValues.竞技场.time} </div>
                                    </Col>

                                    <DatePicker
                                        title='选择挑战时间'
                                        type='time'
                                        defaultValue={new Date(`2025-05-01 ${formValues.竞技场.time}`)}
                                        visible={visible.arenaTime}
                                        onClose={() => setVisible({ arenaTime: false })}
                                        onConfirm={values => onFormChange('arenaTime', values)}
                                        filter={(type, options) => {
                                            if (type === 'hour') {
                                                return options.filter(option => option.value >= 6 && option.value < 22);
                                            }
                                            return options;
                                        }}
                                    />
                                </Row>
                            </>
                        )}
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16'>挑战次数</Col>
                            <Col
                                span='8'
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}>
                                <InputNumber
                                    value={formValues.竞技场.count}
                                    allowEmpty
                                    onChange={values => onFormChange('竞技场次数', values)}
                                    max={20}
                                    min={0}
                                />
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16'>挑战模式</Col>
                            <Col
                                span='14'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ arenaMode: true })}>
                                <div>{arenaModeOptions.find(item => item.value === formValues.竞技场.mode).text} </div>
                            </Col>
                            <Picker
                                title='挑战模式'
                                visible={visible.arenaMode}
                                options={arenaModeOptions}
                                value={[arenaModeOptions.find(item => item.value === formValues.竞技场.mode).value]}
                                onConfirm={values => onFormChange('arenaMode', values)}
                                onClose={() => setVisible({ arenaMode: false })}
                            />
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>预设阵容</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.竞技场.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('竞技场:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: '竞技场:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.竞技场.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>

                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col
                                span='18'
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                月度竞技场
                                <Ask
                                    style={{ marginLeft: '5px' }}
                                    onClick={() => {
                                        Dialog.alert({
                                            content: `每个月的最后一天的第一次上线时将会做月度竞技场, 请保证有充足的咸神门票`,
                                            confirmText: '确认',
                                            // cancelText: '取消',
                                            lockScroll: false,
                                            hideCancelButton: true,
                                            closeOnOverlayClick: true,
                                        });
                                    }}
                                />
                            </Col>
                            <Col span='6' style={{ textAlign: 'right' }}>
                                <Switch
                                    style={{ float: 'right' }}
                                    checked={formValues.竞技场.monthly}
                                    onChange={value => onFormChange('月度竞技场', value)}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Cell>

            <h2 id='section-日常黑市' style={h2Style}>
                日常黑市
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='6'>黑市购买</Col>
                    <Col span='18' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.buyGoods.open}
                            onChange={value => onFormChange('buyGoods', value)}
                        />
                    </Col>
                </Row>
                {formValues.buyGoods.open && (
                    <>
                        {!formValues.secureMode.open && (
                            <>
                                <Divider />
                                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                                    <Col span='16'>购买时间</Col>
                                    <Col
                                        span='8'
                                        style={{ textAlign: 'right' }}
                                        onClick={() => setVisible({ buyGoodsTime: true })}>
                                        <div>{formValues.buyGoods.time} </div>
                                        {/* <ArrowRight /> */}
                                    </Col>
                                    <DatePicker
                                        title='选择购买时间'
                                        type='time'
                                        defaultValue={new Date(`2025-05-01 ${formValues.buyGoods.time}`)}
                                        visible={visible.buyGoodsTime}
                                        onClose={() => setVisible({ buyGoodsTime: false })}
                                        onConfirm={values => onFormChange('buyGoodsTime', values)}
                                    />
                                </Row>
                            </>
                        )}
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col
                                span='18'
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                折扣与购买次数
                            </Col>
                            <Col span='6' style={{ textAlign: 'right' }}>
                                {/* icon={<SettingIcon />} */}
                                <Button type='info' size='mini' onClick={() => setVisible({ buyGoods: true })}>
                                    设置
                                </Button>
                            </Col>
                            <Popup
                                closeable
                                title='日常黑市设置'
                                visible={visible.buyGoods}
                                style={{ height: '75%' }}
                                position='bottom'
                                onClose={() => setVisible({ buyGoods: false })}>
                                <div style={{ marginBottom: '10px', padding: '0 10px', textAlign: 'right' }}>
                                    <Ask
                                        style={{ marginRight: '5px', verticalAlign: 'middle' }}
                                        onClick={() => {
                                            Dialog.alert({
                                                content: (
                                                    <>
                                                        <p>
                                                            最多购买次数：包含每天1次自动刷新及1次免费刷新。如果次数大于2，则后续每次刷新需要花费100元宝。每次刷新只购买符合折扣要求的商品。
                                                        </p>
                                                        <p>
                                                            折扣：0折是不购买, 10折是必买,
                                                            其余将会以指定折扣或更低的价格购买。例：您设置了7折的黄金鱼竿,
                                                            将会购买7折或者7折以下的黄金鱼竿。
                                                        </p>
                                                        <p>
                                                            至少购买次数：即使是没有符合折扣要求的商品，也会按照此项设置购买指定的次数。当购买符合折扣要求的商品已经达到“至少购买次数”时，不再购买高于预期折扣的商品。“至少购买次数”不能超过“最多购买次数”。
                                                        </p>
                                                        <p>
                                                            例如：在攒金鱼竿时，如果需要每天至少购买一次金鱼竿，推荐设置“最多购买次数”为2，金鱼竿“折扣”为7折，金鱼竿“至少购买次数”为1。此时如果第一次自动刷新后金鱼竿为原价，则第一轮不会购买，而会在免费刷新一次后购买一次金鱼竿。如果第一次自动刷新后金鱼竿为6折，则立即购买，在免费刷新后如果价格仍然≤7折才会购买。
                                                        </p>
                                                        <p>每周五0点-12点不购买日常黑市中的商品。</p>
                                                        <p style={{ color: '#fa8c16', fontWeight: 'bold' }}>
                                                            多场景策略说明：系统会根据当前活动状态自动选择对应的购买策略。金鱼悬赏活动优先级最高，其次是金砖达标活动，最后是无活动策略。
                                                        </p>
                                                    </>
                                                ),
                                                confirmText: '确认',
                                                lockScroll: false,
                                                hideCancelButton: true,
                                                closeOnOverlayClick: true,
                                            });
                                        }}
                                    />
                                    <span style={{ fontSize: '14px', color: '#666' }}>点击查看说明</span>
                                </div>
                                <Tabs value={buyGoodsActiveTab} onChange={value => setBuyGoodsActiveTab(value)}>
                                    <Tabs.TabPane title='无消耗活动' value='default'>
                                        <Row
                                            type='flex'
                                            wrap='nowrap'
                                            justify='center'
                                            align='center'
                                            style={{ paddingBottom: '10px', paddingTop: '10px' }}>
                                            <Col
                                                span='24'
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    flexDirection: 'row',
                                                }}>
                                                <div style={{ marginRight: '10px' }}>最多购买次数</div>
                                                <InputNumber
                                                    value={formValues.buyGoods.strategies?.default?.refresh}
                                                    onChange={values => onFormChange('buyGoodsRefresh:default', values)}
                                                    max={maxBuyCount}
                                                    min={0}
                                                />
                                                （刷新黑市共需要花费
                                                {Math.max(
                                                    (formValues.buyGoods.strategies?.default?.refresh - 2) * 100,
                                                    0
                                                )}
                                                元宝）
                                            </Col>
                                        </Row>
                                        {renderBuyGoodsGrid('default')}
                                    </Tabs.TabPane>
                                    <Tabs.TabPane title='金砖达标' value='金砖达标'>
                                        <Row
                                            type='flex'
                                            wrap='nowrap'
                                            justify='center'
                                            align='center'
                                            style={{ paddingBottom: '10px', paddingTop: '10px' }}>
                                            <Col
                                                span='24'
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    flexDirection: 'row',
                                                }}>
                                                <div style={{ marginRight: '10px' }}>最多购买次数</div>
                                                <InputNumber
                                                    value={formValues.buyGoods.strategies?.金砖达标?.refresh}
                                                    allowEmpty
                                                    onChange={values =>
                                                        onFormChange('buyGoodsRefresh:金砖达标', values)
                                                    }
                                                    max={maxBuyCount}
                                                    min={0}
                                                />
                                                （刷新黑市共需要花费
                                                {Math.max(
                                                    (formValues.buyGoods.strategies?.金砖达标?.refresh - 2) * 100,
                                                    0
                                                )}
                                                元宝）
                                            </Col>
                                        </Row>
                                        {renderBuyGoodsGrid('金砖达标')}
                                    </Tabs.TabPane>
                                    <Tabs.TabPane title='金鱼悬赏' value='goldenFish'>
                                        <Row
                                            type='flex'
                                            wrap='nowrap'
                                            justify='center'
                                            align='center'
                                            style={{ paddingBottom: '10px', paddingTop: '10px' }}>
                                            <Col
                                                span='24'
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    flexDirection: 'row',
                                                }}>
                                                <div style={{ marginRight: '10px' }}>最多购买次数</div>
                                                <InputNumber
                                                    value={formValues.buyGoods.strategies?.goldenFish?.refresh}
                                                    allowEmpty
                                                    onChange={values =>
                                                        onFormChange('buyGoodsRefresh:goldenFish', values)
                                                    }
                                                    max={maxBuyCount}
                                                    min={0}
                                                />
                                                （刷新黑市共需要花费
                                                {Math.max(
                                                    (formValues.buyGoods.strategies?.goldenFish?.refresh - 2) * 100,
                                                    0
                                                )}
                                                元宝）
                                            </Col>
                                        </Row>
                                        {renderBuyGoodsGrid('goldenFish')}
                                    </Tabs.TabPane>
                                </Tabs>
                            </Popup>
                        </Row>
                    </>
                )}
            </Cell>

            <h2 id='section-咸王梦境' style={h2Style}>
                咸王梦境
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='6'>咸王梦境</Col>
                    <Col span='18' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.dungeon.open}
                            onChange={value => onFormChange('dungeon', value)}
                        />
                    </Col>
                </Row>
                {formValues.dungeon.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col
                                span='18'
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                梦境商店
                            </Col>
                            <Col span='6' style={{ textAlign: 'right' }}>
                                <Button type='info' size='mini' onClick={() => setVisible({ dungeonShop: true })}>
                                    设置
                                </Button>
                            </Col>
                            <Popup
                                closeable
                                title='梦境商店'
                                visible={visible.dungeonShop}
                                style={{ height: '75%' }}
                                position='bottom'
                                onClose={() => setVisible({ dungeonShop: false })}>
                                <Tabs
                                    defaultValue={0}
                                    onChange={value => {
                                        console.log(value);
                                    }}>
                                    <Tabs.TabPane title='初级商人'>{renderDungeonShopItems('1')}</Tabs.TabPane>
                                    <Tabs.TabPane title='中级商人'>{renderDungeonShopItems('2')}</Tabs.TabPane>
                                    <Tabs.TabPane title='高级商人'>{renderDungeonShopItems('3')}</Tabs.TabPane>
                                </Tabs>
                            </Popup>
                        </Row>
                    </>
                )}
            </Cell>

            <h2 id='section-灯神挑战' style={h2Style}>
                灯神挑战
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>领扫荡券</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.领扫荡券.open}
                            onChange={value => onFormChange('领扫荡券', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>扫荡灯神</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.扫荡灯神.open}
                            onChange={value => onFormChange('扫荡灯神', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>灯神挑战</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.灯神挑战.open}
                            onChange={value => onFormChange('灯神挑战', value)}
                        />
                    </Col>
                </Row>
                {formValues.灯神挑战.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16'>挑战国家</Col>
                            <Col
                                span='14'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ lightState: true })}>
                                <div>
                                    {lightTeamOptions.find(item => item.value === formValues.灯神挑战.state).text}{' '}
                                </div>
                            </Col>
                            <Picker
                                title='挑战国家'
                                visible={visible.lightState}
                                options={lightTeamOptions}
                                value={[lightTeamOptions.find(item => item.value === formValues.灯神挑战.state).value]}
                                onConfirm={values => onFormChange('lightState', values)}
                                onClose={() => setVisible({ lightState: false })}
                            />
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>预设阵容</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.灯神挑战.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('灯神挑战:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: '灯神挑战:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.灯神挑战.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
            </Cell>

            <h2 id='section-深海挑战' style={h2Style}>
                深海挑战
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>扫荡深海</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.扫荡深海.open}
                            onChange={value => onFormChange('扫荡深海', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>深海挑战</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.深海挑战.open}
                            onChange={value => onFormChange('深海挑战', value)}
                        />
                    </Col>
                </Row>
                {formValues.深海挑战.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>预设阵容</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.深海挑战.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('深海挑战:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: '深海挑战:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.深海挑战.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
            </Cell>

            <h2 id='section-盐场设置' style={h2Style}>
                盐场设置
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        暂停上号
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: (
                                        <>
                                            <p>{`开启暂停上号功能后, 盐场时间暂停挤号, 所有上号时间在这个期间内, 将会推迟到21点整`}</p>
                                            <p>{`例: 领取挂机奖励在周六的20点30分执行, 但是因为是盐场时间, 将会推迟到21点`}</p>
                                        </>
                                    ),
                                    confirmText: '确认',
                                    // cancelText: '取消',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={!formValues.war.open}
                            onChange={value => onFormChange('war', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        报名
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.legion.battleField.signUp.open}
                            onChange={value => onFormChange('legion.battleField.signUp.open', value)}
                        />
                    </Col>
                </Row>
                {formValues.legion.battleField.signUp.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='16'>报名时间</Col>
                            <Col
                                span='8'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ battleFieldSignUpTime: true })}>
                                <div>{formValues.legion.battleField.signUp.time}</div>
                            </Col>
                            <DatePicker
                                title='选择盐场报名时间'
                                type='hour-minutes'
                                defaultValue={new Date(`2025-05-01 ${formValues.legion.battleField.signUp.time}`)}
                                visible={visible.battleFieldSignUpTime}
                                onClose={() => setVisible({ battleFieldSignUpTime: false })}
                                onConfirm={(options, values) => onFormChange('legion.battleField.signUp.time', options)}
                                filter={(type, options) => {
                                    if (type === 'hour') {
                                        return options.filter(option => option.value < 18 && option.value >= 8);
                                    }
                                    return options;
                                }}
                            />
                        </Row>
                    </>
                )}
            </Cell>

            <h2 id='section-每日任务' style={h2Style}>
                每日任务
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>免费招募</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.招募.free.open}
                            onChange={value => onFormChange('招募:free', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>
                        招募两次
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `开启后只有在当日活跃积分≥85分时才进行第二次招募`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.招募.twice}
                            onChange={value => onFormChange('招募:twice', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>免费点金</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.buyGold.open}
                            onChange={value => onFormChange('buyGold', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16'>赠送金币</Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.领取好友金币.open}
                            onChange={value => onFormChange('领取好友金币', value)}
                        />
                    </Col>
                </Row>

                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        开启宝箱
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `* 如果钻石宝箱有至少3个，则打开1次钻石宝箱。\n* 如果未领取的宝箱积分奖励中有足够的钻石宝箱，则领取到3个钻石宝箱后打开1次钻石宝箱。\n* 如果领取完宝箱积分奖励也不够3个钻石宝箱，则打开1次木质宝箱。`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.开箱.open}
                            onChange={value => onFormChange('开箱', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        活跃奖励
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `日活跃和周活跃`,
                                    confirmText: '确认',
                                    // cancelText: '取消',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.dailyReward.open}
                            onChange={value => onFormChange('dailyReward', value)}
                        />
                    </Col>
                </Row>
            </Cell>

            <h2 id='section-单周活动' style={h2Style}>
                单周活动
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        免费福利
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `领取招募福利、宝箱福利、江湖黑市福利`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>

                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.welfare?.open ?? true}
                            onChange={value => onFormChange('activityWelfare', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        免费扭蛋抽奖
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `每周二、四、六自动进行一次免费扭蛋机抽奖`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>

                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.gacha?.free}
                            onChange={value => onFormChange('activity:gacha:free', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        招募达标 - 自选大奖
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `设置招募达标活动的自选大奖兑换数量。当可领次数不足时，按从上到下的顺序依次兑换。设置为0表示不兑换该道具。`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.recruit.selectRewardsOpen}
                            onChange={value => onFormChange('activity:recruit:selectRewardsOpen', value)}
                        />
                    </Col>
                </Row>
                {formValues.activity.recruit.selectRewardsOpen && (
                    weekActRewards.recruit.length > 0 ? (
                        weekActRewards.recruit.map((reward, index) => (
                            <div key={reward.itemId} style={{ width: '100%' }}>
                                <Divider />
                                <Row type='flex' wrap='nowrap' justify='center' align='center' style={{ paddingBottom: '10px' }}>
                                    <Col span='1'></Col>
                                    <Col span='13' style={{ display: 'flex', alignItems: 'center' }}>
                                        {reward.itemName || `道具${reward.itemId}`}
                                    </Col>
                                    <Col span='10' style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <InputNumber
                                            value={formValues.activity.recruit.selectRewards?.[reward.itemId] ?? 0}
                                            onChange={v => onFormChange(`activity:recruit:selectRewards:${reward.itemId}`, v)}
                                            min={0}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        ))
                    ) : (
                        <Row type='flex' wrap='nowrap' justify='center' align='center' style={{ paddingBottom: '10px' }}>
                            <Col span='1'></Col>
                            <Col span='23' style={{ color: '#999', fontSize: '12px' }}>
                                暂无奖励数据
                            </Col>
                        </Row>
                    )
                )}
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        江湖黑市
                    </Col>

                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.buystoregoods.open}
                            onChange={value => onFormChange('activity:buystoregoods', value)}
                        />
                    </Col>
                </Row>
                {formValues.activity.buystoregoods.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='12'>江湖黑市礼包设置</Col>

                            <Col span='11' style={{ textAlign: 'right' }}>
                                <Button
                                    style={{ marginLeft: '10px' }}
                                    type='info'
                                    size='mini'
                                    onClick={() =>
                                        setVisible({ activityBuystoregoods: true })
                                    } /*icon={<SettingIcon />}*/
                                >
                                    设置
                                </Button>
                            </Col>
                            <Popup
                                closeable
                                title='江湖黑市'
                                visible={visible.activityBuystoregoods}
                                style={{ height: '75%' }}
                                position='bottom'
                                onClose={() => setVisible({ activityBuystoregoods: false })}>
                                <div style={{ padding: '24px 20px' }}>
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            礼包内容
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            售价
                                        </Col>
                                        <Col span='4' style={{ display: 'flex', alignItems: 'center' }}>
                                            购买1次
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            500金砖（已包含在“单周活动”-&gt;“免费福利”设置中）
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            免费
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.welfare?.open ?? true}
                                                readonly
                                                disabled
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            招募令×5，精铁×1000
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            600金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['1']}
                                                onChange={value => onFormChange('activity:buystoregoods:1', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            招募令×10，进阶石×2000
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            1200金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['2']}
                                                onChange={value => onFormChange('activity:buystoregoods:2', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            进阶石×6000
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            2500金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['3']}
                                                onChange={value => onFormChange('activity:buystoregoods:3', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row type='flex' justify='center' align='center' style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'normal' }}>
                                            铂金宝箱×10，黄金宝箱×10，青铜宝箱×10，木质宝箱×10
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            5000金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['4']}
                                                onChange={value => onFormChange('activity:buystoregoods:4', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            招募令×40，随机红将碎片×50
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            8000金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['5']}
                                                onChange={value => onFormChange('activity:buystoregoods:5', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            普通鱼竿×30，黄金鱼竿×30
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            12000金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['6']}
                                                onChange={value => onFormChange('activity:buystoregoods:6', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            白玉×2000
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            2000金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['7']}
                                                onChange={value => onFormChange('activity:buystoregoods:7', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            灵贝×10
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            25000金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['8']}
                                                onChange={value => onFormChange('activity:buystoregoods:8', value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Row
                                        type='flex'
                                        wrap='nowrap'
                                        justify='center'
                                        align='center'
                                        style={{ paddingBottom: '10px' }}>
                                        <Col span='14' style={{ display: 'flex', alignItems: 'center' }}>
                                            梦魇晶石×2000，玩具扳手×3000
                                        </Col>
                                        <Col span='6' style={{ textAlign: 'center' }}>
                                            8000金砖
                                        </Col>
                                        <Col span='4' style={{ textAlign: 'right' }}>
                                            <Switch
                                                style={{ float: 'right' }}
                                                checked={formValues.activity.buystoregoods.shop['9']}
                                                onChange={value => onFormChange('activity:buystoregoods:9', value)}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            </Popup>
                        </Row>
                    </>
                )}

                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='12' style={{ display: 'flex', alignItems: 'center' }}>
                        宝箱达标
                    </Col>
                    <Col span='12' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.box.open}
                            onChange={value => onFormChange('activity:box', value)}
                        />
                    </Col>
                </Row>
                {formValues.activity.box.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='12'>
                                宝箱达标完成轮数
                                <Ask
                                    style={{ marginLeft: '5px' }}
                                    onClick={() => {
                                        Dialog.alert({
                                            content: `“完成轮数”为最多完成的轮数。如果实际宝箱积分不足以完成指定轮数，则完成尽可能多的整轮活动。例如设置“完成轮数”为3，开完所有宝箱共计19000分，那么会在完成2轮宝箱即16000分达标活动后停止开宝箱。`,
                                            confirmText: '确认',
                                            // cancelText: '取消',
                                            lockScroll: false,
                                            hideCancelButton: true,
                                            closeOnOverlayClick: true,
                                        });
                                    }}
                                />
                            </Col>
                            <Col
                                span='11'
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}>
                                <InputNumber
                                    value={formValues.activity.box.rounds}
                                    onChange={values => onFormChange('activity:box:rounds', values)}
                                    max={4}
                                    min={0}
                                />
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='12'>
                                日期范围
                                <Ask
                                    style={{ marginLeft: '5px' }}
                                    onClick={() => {
                                        Dialog.alert({
                                            content: `选择允许做宝箱达标活动的时间范围。由于周五做活动的人多会限流，暂不支持周五做宝箱达标活动。`,
                                            confirmText: '确认',
                                            // cancelText: '取消',
                                            lockScroll: false,
                                            hideCancelButton: true,
                                            closeOnOverlayClick: true,
                                        });
                                    }}
                                />
                            </Col>
                            <Col
                                span='11'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ boxActivityWeekdayPicker: true })}>
                                <div>
                                    {
                                        boxActivityWeekdayOptions.find(
                                            item => item.value === formValues.activity.box.afterWeekday
                                        ).text
                                    }{' '}
                                </div>
                            </Col>
                            <Picker
                                title='宝箱达标日期范围'
                                visible={visible.boxActivityWeekdayPicker}
                                options={boxActivityWeekdayOptions}
                                value={[
                                    boxActivityWeekdayOptions.find(
                                        item => item.value === formValues.activity.box.afterWeekday
                                    ).value,
                                ]}
                                onConfirm={values => onFormChange('activity:box:afterWeekday', values[0].value)}
                                onClose={() => setVisible({ boxActivityWeekdayPicker: false })}
                            />
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='12'>任意咸将达到星级后停止宝箱达标</Col>
                            <Col
                                span='11'
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}>
                                <InputNumber
                                    value={formValues.activity.box.stopStars}
                                    onChange={values => onFormChange('activity:box:stopStars', values)}
                                    formatter={heroStarFormatter}
                                    style={{
                                        '--nutui-input-background-color': '#f7f8fa',
                                        '--nutui-inputnumber-input-width': '100px',
                                    }}
                                    max={31}
                                    min={0}
                                />
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='15' style={{ display: 'flex', alignItems: 'center' }}>
                                宝箱达标 - 自选大奖
                                <Ask
                                    style={{ marginLeft: '5px' }}
                                    onClick={() => {
                                        Dialog.alert({
                                            content: `设置宝箱达标活动的自选大奖兑换数量。当可领次数不足时，按从上到下的顺序依次兑换。设置为0表示不兑换该道具。`,
                                            confirmText: '确认',
                                            lockScroll: false,
                                            hideCancelButton: true,
                                            closeOnOverlayClick: true,
                                        });
                                    }}
                                />
                            </Col>
                            <Col span='8' style={{ textAlign: 'right' }}>
                                <Switch
                                    style={{ float: 'right' }}
                                    checked={formValues.activity.box.selectRewardsOpen}
                                    onChange={value => onFormChange('activity:box:selectRewardsOpen', value)}
                                />
                            </Col>
                        </Row>
                        {formValues.activity.box.selectRewardsOpen && (
                            weekActRewards.box.length > 0 ? (
                                weekActRewards.box.map((reward, index) => (
                                    <div key={reward.itemId} style={{ width: '100%' }}>
                                        <Divider />
                                        <Row type='flex' wrap='nowrap' justify='center' align='center' style={{ paddingBottom: '10px' }}>
                                            <Col span='2'></Col>
                                            <Col span='12' style={{ display: 'flex', alignItems: 'center' }}>
                                                {reward.itemName || `道具${reward.itemId}`}
                                            </Col>
                                            <Col span='10' style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <InputNumber
                                                    value={formValues.activity.box.selectRewards?.[reward.itemId] ?? 0}
                                                    onChange={v => onFormChange(`activity:box:selectRewards:${reward.itemId}`, v)}
                                                    min={0}
                                                />
                                            </Col>
                                        </Row>
                                    </div>
                                ))
                            ) : (
                                <Row type='flex' wrap='nowrap' justify='center' align='center' style={{ paddingBottom: '10px' }}>
                                    <Col span='1'></Col>
                                    <Col span='23' style={{ color: '#999', fontSize: '12px' }}>
                                        暂无奖励数据
                                    </Col>
                                </Row>
                            )
                        )}
                    </>
                )}
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='16' style={{ display: 'flex', alignItems: 'center' }}>
                        金砖达标 - 自选大奖
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `设置金砖达标活动的自选大奖兑换数量。当可领次数不足时，按从上到下的顺序依次兑换。设置为0表示不兑换该道具。`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='8' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.gold.selectRewardsOpen}
                            onChange={value => onFormChange('activity:gold:selectRewardsOpen', value)}
                        />
                    </Col>
                </Row>
                {formValues.activity.gold.selectRewardsOpen && (
                    weekActRewards.gold.length > 0 ? (
                        weekActRewards.gold.map((reward, index) => (
                            <div key={reward.itemId} style={{ width: '100%' }}>
                                <Divider />
                                <Row type='flex' wrap='nowrap' justify='center' align='center' style={{ paddingBottom: '10px' }}>
                                    <Col span='1'></Col>
                                    <Col span='13' style={{ display: 'flex', alignItems: 'center' }}>
                                        {reward.itemName || `道具${reward.itemId}`}
                                    </Col>
                                    <Col span='10' style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <InputNumber
                                            value={formValues.activity.gold.selectRewards?.[reward.itemId] ?? 0}
                                            onChange={v => onFormChange(`activity:gold:selectRewards:${reward.itemId}`, v)}
                                            min={0}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        ))
                    ) : (
                        <Row type='flex' wrap='nowrap' justify='center' align='center' style={{ paddingBottom: '10px' }}>
                            <Col span='1'></Col>
                            <Col span='23' style={{ color: '#999', fontSize: '12px' }}>
                                暂无奖励数据
                            </Col>
                        </Row>
                    )
                )}
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='12' style={{ display: 'flex', alignItems: 'center' }}>
                        怪异咸将塔
                    </Col>
                    <Col span='12' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.evoTower.open}
                            onChange={value => onFormChange('activity:evoTower:open', value)}
                        />
                    </Col>
                </Row>
                {formValues.activity.evoTower.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='7'>怪异咸将塔阵容</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.activity.evoTower.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('activity:evoTower:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='7'>阵容描述</Col>
                            <Col
                                span='16'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: 'activity:evoTower:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.activity.evoTower.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='7'>
                                指定俱乐部
                                <Ask
                                    style={{ marginLeft: '5px' }}
                                    onClick={() => {
                                        Dialog.alert({
                                            content: `如果填写了俱乐部名称或ID，则只有当前角色在此俱乐部中时才挑战怪异咸将塔。`,
                                            confirmText: '确认',
                                            // cancelText: '取消',
                                            lockScroll: false,
                                            hideCancelButton: true,
                                            closeOnOverlayClick: true,
                                        });
                                    }}
                                />
                            </Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Input
                                    style={{
                                        '--nutui-input-background-color': '#f7f8fa',
                                        '--nutui-input-padding': '5px 5px',
                                    }}
                                    placeholder='输入俱乐部名或ID'
                                    clearable
                                    value={formValues.activity.evoTower.legionNameOrId}
                                    maxLength={10}
                                    align={'center'}
                                    onChange={value => onFormChange('activity:evoTower:legionNameOrId', value)}
                                />
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='7'>怪异寻宝</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Switch
                                    style={{ float: 'right' }}
                                    checked={formValues.activity.evoTower.mergeBox}
                                    onChange={value => onFormChange('activity:evoTower:mergeBox', value)}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Cell>
            <h2 id='section-不定期活动' style={h2Style}>
                不定期活动
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8'>挑战活动预设阵容</Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Radio.Group
                            value={formValues.activity.team?.teamId}
                            shape='button'
                            onChange={value => onFormChange('activity:team:teamId', value)}
                            direction='horizontal'
                            style={{
                                textAlign: 'right',
                                '--nutui-radiogroup-radio-margin': '8px',
                                '--nutui-radio-button-padding': '4px 8px',
                            }}>
                            {defaultTeamList.map(option => (
                                <Radio key={option.value} value={option.value}>
                                    {option.text}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='2'></Col>
                    <Col span='10'>阵容描述</Col>
                    <Col
                        span='12'
                        style={{ textAlign: 'right' }}
                        onClick={() => setVisible({ teamDescPicker: 'activity:team:teamDescId' })}
                    >
                        <span style={{ color: '#1989fa' }}>
                            {getTeamDescName(formValues.activity.team?.teamDescId)}
                        </span>
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='12' style={{ display: 'flex', alignItems: 'center' }}>
                        挑战活动（6个boss）
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `挑战活动：6个boss每天依次开放一个，最后一天开放全部boss。开启后使用"不定期活动->挑战活动预设阵容"进行挑战。`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='12' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.multiTower.open}
                            onChange={value => onFormChange('activity:multiTower:open', value)}
                        />
                    </Col>
                </Row>
                {/* <Divider />
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="12"
                        style={{ display: "flex", alignItems: "center" }}>
                        灵珠降世
                    </Col>
                    <Col span="12" style={{ textAlign: "right" }}>
                        <Switch
                            style={{ float: "right" }}
                            checked={formValues.activity.mine.open}
                            onChange={value =>
                                onFormChange("activity:mine:open", value)
                            }/>
                    </Col>
                </Row>
                {formValues.activity.mine.open && (<>
                    <Divider />
                    <Row type="flex" wrap="nowrap" justify="center" align="center">
                        <Col span="1"></Col>
                        <Col span="12">挑战
                            <Ask style={{ marginLeft: "5px" }}
                                onClick={() => {
                                    Dialog.alert({
                                        content: `使用主线阵容进行挑战。`,
                                        confirmText: "确认",
                                        // cancelText: '取消',
                                        lockScroll: false,
                                        hideCancelButton: true,
                                        closeOnOverlayClick: true,
                                    });
                                }} />
                        </Col>
                        <Col span="11" style={{
                                display: "flex",
                                justifyContent: "flex-end", }} >
                            <Switch
                                style={{ float: "right" }}
                                checked={formValues.activity.mine.tower}
                                onChange={value =>
                                    onFormChange("activity:mine:tower", value)
                            }/>
                        </Col>
                    </Row>
                    <Divider />
                    <Row type="flex" wrap="nowrap" justify="center" align="center">
                        <Col span="1"></Col>
                        <Col span="12">抽奖
                        </Col>
                        <Col span="11" style={{
                                display: "flex",
                                justifyContent: "flex-end", }} >
                            <Switch
                                style={{ float: "right" }}
                                checked={formValues.activity.mine.lottery}
                                onChange={value =>
                                    onFormChange("activity:mine:lottery", value)
                            }/>
                        </Col>
                    </Row> */}
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='12' style={{ display: 'flex', alignItems: 'center' }}>
                        狂野西部
                    </Col>
                    <Col span='12' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.eGame.open}
                            onChange={value => onFormChange('activity:eGame:open', value)}
                        />
                    </Col>
                </Row>
                {formValues.activity.eGame.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='1'></Col>
                            <Col span='12'>抽奖</Col>
                            <Col
                                span='11'
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}>
                                <Switch
                                    style={{ float: 'right' }}
                                    checked={formValues.activity.eGame.lottery}
                                    onChange={value => onFormChange('activity:eGame:lottery', value)}
                                />
                            </Col>
                        </Row>
                    </>
                )}
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='12' style={{ display: 'flex', alignItems: 'center' }}>
                        咸来入镜
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `咸来入镜：补齐每日任务、领取奖励，并把持有的胶卷全部放入以推进拍摄阶段。`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='12' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.activity.photo?.open}
                            onChange={value => onFormChange('activity:photo:open', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="12"
                        style={{ display: "flex", alignItems: "center" }}>
                        2026五周年庆
                    </Col>
                    <Col span="12" style={{ textAlign: "right" }}>
                        <Switch
                            style={{ float: "right" }}
                            checked={formValues.activity.charge.open}
                            onChange={value =>
                                onFormChange("activity:charge:open", value)
                            }/>
                    </Col>
                </Row>
                {formValues.activity.charge.open && (<>
                    <Divider />
                    <Row type="flex" wrap="nowrap" justify="center" align="center">
                        <Col span="1"></Col>
                        <Col span="12">供奉奖励&赐福奖励<Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `包括供奉奖励和赐福奖励。\n* 供奉奖励：只使用仅当期有效的活动道具，不使用路路通。\n* 赐福奖励：除了万能碎片和随机碎片之外，选择右边的奖励。`,
                                    confirmText: '确认',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                        </Col>
                        <Col span="11" style={{
                                display: "flex",
                                justifyContent: "flex-end", }} >
                            <Switch
                                style={{ float: "right" }}
                                checked={formValues.activity.charge.lottery}
                                onChange={value =>
                                    onFormChange("activity:charge:lottery", value)
                            }/>
                        </Col>
                    </Row>
                    <Divider />
                    <Row type="flex" wrap="nowrap" justify="center" align="center">
                        <Col span="1"></Col>
                        <Col span="12">兑换道具{exchangeActivityConfig.taskStartTime ? `（${new Date(exchangeActivityConfig.taskStartTime * 1000).toLocaleString('zh-CN', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}开放）` : ''}
                        </Col>
                        <Col span="11" style={{
                                display: "flex",
                                justifyContent: "flex-end", }} >
                            <Switch
                                style={{ float: "right" }}
                                checked={formValues.activity.charge.exchange?.open || false}
                                onChange={value =>
                                    onFormChange("activity:charge:exchange:open", value)
                            }/>
                        </Col>
                    </Row>
                    {formValues.activity.charge.exchange?.open && (
                        <>
                            <Divider />
                            <Row>
                                <Col span="12">
                                    现有{exchangeActivityConfig.activity?.itemName ?? '活动道具'}：{user.role_info?.items?.[exchangeActivityConfig.activity?.itemName ?? '活动道具'] ?? '0'}
                                </Col>
                                <Col span="12">
                                </Col>
                            </Row>
                            <Divider />
                            <Row>
                                <Col span="24">
                                    <div>兑换目标列表（按由上到下顺序，道具不足时继续尝试兑换下一个）：</div>
                                    <Row type="flex" align="center">
                                        <Col span="10">道具名称</Col>
                                        <Col span="7">累计兑换次数</Col>
                                        <Col span="7">调整顺序</Col>
                                    </Row>
                                    {formValues.activity.charge.exchange?.targets?.map((target, index) => {
                                        const conf = exchangeActivityConfig.exchangeList.find(c => c.id === target.goodsId);
                                        if (!conf) return null;
                                        return (
                                            <div key={index} style={{
                                                border: '1px solid #eee',
                                                borderRadius: '8px',
                                                padding: '10px',
                                                marginBottom: '10px',
                                                backgroundColor: '#f9f9f9'
                                            }}>
                                                <Row type="flex" align="center">
                                                    <Col span="10">
                                                        <div style={{ fontWeight: 'bold' }}>
                                                            {conf.rewardName}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                                            需要{conf.consumeNum}个{exchangeActivityConfig.activity?.itemName ?? '活动道具'} | 限{conf.limitNum}次
                                                            {conf.unlockNeed > 0 && `（累计消耗${conf.unlockNeed}个${exchangeActivityConfig.activity?.itemName ?? '活动道具'}解锁）`}
                                                        </div>
                                                    </Col>
                                                    <Col span="7">
                                                    <InputNumber
                                                        value={target.count}
                                                        min={1}
                                                        max={conf.limitNum}
                                                        onChange={value => onFormChange(`activity:charge:exchange:count:${index}`, parseInt(value) || 1)}
                                                        style={{ width: '100px', marginRight: '5px' }}
                                                    />
                                                    </Col>
                                                    <Col span="7">
                                                    <Button
                                                        type="default"
                                                        size="mini"
                                                        disabled={index === 0}
                                                        onClick={() => onFormChange(`activity:charge:exchange:moveUp:${index}`, null)}
                                                        style={{ marginRight: '5px' }}
                                                    >
                                                        ↑
                                                    </Button>
                                                    <Button
                                                        type="default"
                                                        size="mini"
                                                        disabled={index === formValues.activity.charge.exchange.targets.length - 1}
                                                        onClick={() => onFormChange(`activity:charge:exchange:moveDown:${index}`, null)}
                                                        style={{ marginRight: '5px' }}
                                                    >
                                                        ↓
                                                    </Button>
                                                    <Button
                                                        type="danger"
                                                        size="mini"
                                                        onClick={() => onFormChange(`activity:charge:exchange:delete:${index}`, null)}
                                                    >
                                                        X
                                                    </Button>
                                                    </Col>
                                                </Row>
                                            </div>
                                        );
                                    })}
                                    <Button
                                        type="info"
                                        size="normal"
                                        block
                                        onClick={() => setVisible({ chargeExchangeTargetPicker: true })}
                                        style={{ marginTop: '10px' }}
                                    >
                                        + 添加兑换目标
                                    </Button>
                                </Col>
                            </Row>
                            <Popup
                                closeable
                                title="选择兑换道具"
                                visible={visible.chargeExchangeTargetPicker}
                                style={{ height: "60%" }}
                                position="bottom"
                                onClose={() => setVisible({ chargeExchangeTargetPicker: false })}>
                                <div style={{
                                    padding: '10px',
                                    maxHeight: 'calc(60vh - 60px)',
                                    overflowY: 'auto'
                                }}>
                                    {loadingExchangeConfig ? (
                                        <div style={{ textAlign: 'center', padding: '20px' }}>
                                            加载中...
                                        </div>
                                    ) : exchangeActivityConfig.exchangeList.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                            暂无兑换配置
                                        </div>
                                    ) : (
                                        exchangeActivityConfig.exchangeList.map(conf => {
                                        // 检查是否已拥有该金鱼（如果是金鱼类道具）
                                        const goldenFishes = user.role_info?.goldenFishes || [];
                                        const isOwned = goldenFishes.some(fish => {
                                            // 提取鱼名（去除数量标记，如"骇浪5×1"取"骇浪5"）
                                            const fishName = fish.split('×')[0];
                                            const rewardItemName = conf.rewardName.split('×')[0];
                                            return fishName === rewardItemName;
                                        });

                                        return (
                                            <div
                                                key={conf.id}
                                                style={{
                                                    marginBottom: '10px',
                                                    padding: '12px',
                                                    border: '1px solid #e8e8e8',
                                                    borderRadius: '8px',
                                                    backgroundColor: isOwned ? '#f0f0f0' : '#fafafa',
                                                    opacity: isOwned ? 0.6 : 1,
                                                    cursor: isOwned ? 'not-allowed' : 'pointer'
                                                }}
                                                onClick={() => {
                                                    if (!isOwned) {
                                                        setVisible({ chargeExchangeTargetPicker: false });
                                                        onFormChange("activity:charge:exchange:addTarget", parseInt(conf.id));
                                                    }
                                                }}>
                                                <div style={{ fontSize: '14px' }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                        {conf.rewardName}
                                                        {isOwned && <span style={{ color: '#999', marginLeft: '8px' }}>(已拥有)</span>}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        需{conf.consumeNum}个{exchangeActivityConfig.activity?.itemName ?? '活动道具'}，限{conf.limitNum}次
                                                        {conf.unlockNeed > 0 && `（累计消耗${conf.unlockNeed}个${exchangeActivityConfig.activity?.itemName ?? '活动道具'}解锁）`}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                    )}
                                </div>
                            </Popup>
                        </>
                    )}
                </>)}
            </Cell>

            <WeiZhenExchangeView
                weiZhenConfig={weiZhenConfig}
                loading={loadingWeiZhenConfig}
                exchangeConfig={formValues.activity.weiZhenExchange}
                user={user}
                onFormChange={onFormChange}
            />

            <h2 id='section-预设阵容汇总' style={h2Style}>
                预设阵容汇总
            </h2>
            <Cell style={cellStyle}>
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8' style={{ display: 'flex', alignItems: 'center' }}>
                        默认阵容
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `脚本开始执行时、执行完成后都会把阵容切换到默认阵容。`,
                                    confirmText: '确认',
                                    // cancelText: '取消',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Radio.Group
                            value={formValues.team?.teamId}
                            shape='button'
                            onChange={value => onFormChange('team:teamId', value)}
                            direction='horizontal'
                            style={{
                                textAlign: 'right',
                                '--nutui-radiogroup-radio-margin': '8px',
                                '--nutui-radio-button-padding': '4px 8px',
                            }}>
                            {defaultTeamList.map(option => (
                                <Radio key={option.value} value={option.value}>
                                    {option.text}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='2'></Col>
                    <Col span='10'>阵容描述</Col>
                    <Col
                        span='12'
                        style={{ textAlign: 'right' }}
                        onClick={() => setVisible({ teamDescPicker: 'team:teamDescId' })}
                    >
                        <span style={{ color: '#1989fa' }}>
                            {getTeamDescName(formValues.team?.teamDescId)}
                        </span>
                    </Col>
                </Row>
                {formValues.legion.boss.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>俱乐部boss</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.legion.boss.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('legion:boss:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: 'legion:boss:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.legion.boss.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
                {formValues.tower.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>咸将塔</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.tower.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('tower:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: 'tower:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.tower.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
                {formValues.每日咸王.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>每日咸王</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.每日咸王.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('每日咸王:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: '每日咸王:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.每日咸王.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
                {formValues.竞技场.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>竞技场</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.竞技场.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('竞技场:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: '竞技场:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.竞技场.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
                {formValues.灯神挑战.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>阵营灯神挑战</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.灯神挑战.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('灯神挑战:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: '灯神挑战:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.灯神挑战.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
                {formValues.深海挑战.open && (
                    <>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='8'>深海灯神挑战</Col>
                            <Col span='16' style={{ textAlign: 'right' }}>
                                <Radio.Group
                                    value={formValues.深海挑战.team?.teamId}
                                    shape='button'
                                    onChange={value => onFormChange('深海挑战:team:teamId', value)}
                                    direction='horizontal'
                                    style={{
                                        textAlign: 'right',
                                        '--nutui-radiogroup-radio-margin': '8px',
                                        '--nutui-radio-button-padding': '4px 8px',
                                    }}>
                                    {defaultTeamList.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.text}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                        <Divider />
                        <Row type='flex' wrap='nowrap' justify='center' align='center'>
                            <Col span='2'></Col>
                            <Col span='10'>阵容描述</Col>
                            <Col
                                span='12'
                                style={{ textAlign: 'right' }}
                                onClick={() => setVisible({ teamDescPicker: '深海挑战:team:teamDescId' })}
                            >
                                <span style={{ color: '#1989fa' }}>
                                    {getTeamDescName(formValues.深海挑战.team?.teamDescId)}
                                </span>
                            </Col>
                        </Row>
                    </>
                )}
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8'>不定期挑战活动</Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Radio.Group
                            value={formValues.activity.team?.teamId}
                            shape='button'
                            onChange={value => onFormChange('activity:team:teamId', value)}
                            direction='horizontal'
                            style={{
                                textAlign: 'right',
                                '--nutui-radiogroup-radio-margin': '8px',
                                '--nutui-radio-button-padding': '4px 8px',
                            }}>
                            {defaultTeamList.map(option => (
                                <Radio key={option.value} value={option.value}>
                                    {option.text}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='2'></Col>
                    <Col span='10'>阵容描述</Col>
                    <Col
                        span='12'
                        style={{ textAlign: 'right' }}
                        onClick={() => setVisible({ teamDescPicker: 'activity:team:teamDescId' })}
                    >
                        <span style={{ color: '#1989fa' }}>
                            {getTeamDescName(formValues.activity.team?.teamDescId)}
                        </span>
                    </Col>
                </Row>
            </Cell>
            <h2 id='section-其他功能' style={h2Style}>
                其他功能
            </h2>
            <Cell style={cellStyle}>
                {/* <Row type="flex" wrap="nowrap" justify="center" align="center">
                    <Col span="8">领取通行证</Col>
                    <Col span="16" style={{ textAlign: 'right' }}>
                        <Switch style={{ float: 'right' }} checked={formValues.recyclewarorderrewardclaim.open} onChange={value => onFormChange("recyclewarorderrewardclaim", value)} />
                    </Col>
                </Row>
                <Divider /> */}
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8' style={{ display: 'flex', alignItems: 'center' }}>
                        免费礼包
                        <Ask
                            style={{ marginLeft: '5px' }}
                            onClick={() => {
                                Dialog.alert({
                                    content: `签到, 永久卡, 福利卡, 终身卡, 每日特惠`,
                                    confirmText: '确认',
                                    // cancelText: '取消',
                                    lockScroll: false,
                                    hideCancelButton: true,
                                    closeOnOverlayClick: true,
                                });
                            }}
                        />
                    </Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.福利.sign.open}
                            onChange={value => onFormChange('福利', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8'>领取邮件</Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.email.open}
                            onChange={value => onFormChange('email', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8'>删除已读邮件</Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.email.rm.open}
                            onChange={value => onFormChange('email:rm', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8'>合成咸将</Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.hero.synthesize.open}
                            onChange={value => onFormChange('hero:synthesize', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8'>咸将、鱼灵升星</Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.hero.upgradeStar.open}
                            onChange={value => onFormChange('hero:upgradeStar', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col span='8'>领取图鉴奖励</Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Switch
                            style={{ float: 'right' }}
                            checked={formValues.hero.bookReward.open}
                            onChange={value => onFormChange('hero:bookReward', value)}
                        />
                    </Col>
                </Row>
                <Divider />
                <Row type='flex' wrap='nowrap' justify='center' align='center'>
                    <Col
                        span='8'
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}>
                        使用道具
                    </Col>
                    <Col span='16' style={{ textAlign: 'right' }}>
                        <Button type='info' size='mini' onClick={() => setVisible({ useItemsVisible: true })}>
                            设置
                        </Button>
                    </Col>
                    <Popup
                        closeable
                        title='使用道具'
                        visible={visible.useItemsVisible}
                        style={{ height: '75%' }}
                        position='bottom'
                        onClose={() => setVisible({ useItemsVisible: false })}>
                        <Tabs defaultValue={0}>
                            <Tabs.TabPane title='仓库礼包'>
                                {useItemsOptions.map(item => (
                                    <>
                                        <Row
                                            key={item}
                                            type='flex'
                                            wrap='nowrap'
                                            justify='center'
                                            align='center'
                                            style={{ paddingBottom: '10px' }}>
                                            <Col
                                                span='16'
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                {item}
                                            </Col>
                                            <Col span='8' style={{ textAlign: 'right' }}>
                                                <Switch
                                                    style={{ float: 'right' }}
                                                    checked={formValues.useItems[item]}
                                                    onChange={value => onFormChange(`useItems:${item}`, value)}
                                                />
                                            </Col>
                                        </Row>
                                        <Divider />
                                    </>
                                ))}
                            </Tabs.TabPane>
                        </Tabs>
                    </Popup>
                </Row>
            </Cell>

            <div style={{ height: '100px', marginTop: '100px' }}></div>

            <Sticky
                threshold={100}
                position='bottom'
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                {isChanged && (
                    <Button
                        type='primary'
                        style={{ width: '45%' }}
                        onClick={() =>
                            setConfirmDialog({
                                show: true,
                                // title: "确认",
                                content: <div style={{ textAlign: 'center' }}>是否保存设置?</div>,
                                onConfirm: () => {
                                    updateConfig(formValues);
                                    onCancelConfirmDialog();
                                },
                                hideConfirmButton: false,
                                hideCancelButton: false,
                            })
                        }>
                        保存
                    </Button>
                )}
                {isChanged && (
                    <Button
                        type='primary'
                        style={{ width: '45%' }}
                        onClick={() =>
                            setConfirmDialog({
                                show: true,
                                // title: "确认",
                                content: <div style={{ textAlign: 'center' }}>是否重置设置?</div>,
                                onConfirm: () => {
                                    setFormValues(JSON.parse(JSON.stringify(user.config)));
                                    onCancelConfirmDialog();
                                    setIsChanged(false);
                                    onConfigChangeCb(null);
                                },
                                hideConfirmButton: false,
                                hideCancelButton: false,
                            })
                        }>
                        重置
                    </Button>
                )}
            </Sticky>
            {/* 阵容描述通用 Picker */}
            <Picker
                title='选择阵容描述'
                visible={visible.teamDescPicker != null}
                options={[teamDescOptions]}
                value={[getTeamDescIdByPath(visible.teamDescPicker || '') || TEAM_DESC_DEFAULT]}
                onConfirm={values => {
                    const path = visible.teamDescPicker;
                    if (path) {
                        const selectedValue = values[0]?.value === TEAM_DESC_DEFAULT ? '' : values[0]?.value || '';
                        onFormChange(path, selectedValue);
                    }
                    setVisible({ teamDescPicker: null });
                }}
                onClose={() => setVisible({ teamDescPicker: null })}
            />
            {/*  */}
        </>
    );
}

export default Setting;
